import React, { useState, useEffect } from 'react';
import { Lock, Search, RefreshCw, Eye, CheckCircle, Clock, XCircle, DollarSign, Settings, Download, Filter, BarChart3, TrendingUp, Zap, MessageCircle, X, User, Trash2 } from 'lucide-react';
import PageSeo from '../components/PageSeo';

// Helper function to get the correct backend URL
const getBackendUrl = () => {
  return window.location.hostname === 'localhost' 
    ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001')
    : '';  // Empty string = relative URL for production
};

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({ ref: '', status: '', message: '' });
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('submissions'); // 'submissions' or 'settings'
  const [passwordChange, setPasswordChange] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [passwordChangeMessage, setPasswordChangeMessage] = useState({ type: '', text: '' });
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);

  // WebSocket state (fallback to polling if WS fails)
  const [wsConnected, setWsConnected] = useState(false);
  const [autoRefreshActive, setAutoRefreshActive] = useState(true); // Polling fallback
  const [wsInstance, setWsInstance] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);

  const [bulkAction, setBulkAction] = useState({ status: '', message: '' });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    cardBrand: '',
    minValue: '',
    maxValue: '',
    hasReceipt: '',
    fraudScore: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({ total: 0, unique: 0, duplicates: 0 });
  const [purgeStats, setPurgeStats] = useState(null);
  const [newPurgeDays, setNewPurgeDays] = useState(30);
  const [purgeMessage, setPurgeMessage] = useState({ text: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [bulkRejectPreview, setBulkRejectPreview] = useState(null);
  const [bulkRejectMessage, setBulkRejectMessage] = useState({ text: '', type: '' });
  const [customerHistory, setCustomerHistory] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [notesState, setNotesState] = useState({});  // Store note text for each submission
  const [notesLoading, setNotesLoading] = useState({});  // Loading state for each note submission
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);  // Auto-refresh toggle
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(60);  // Refresh interval in seconds
  const [lastRefreshTime, setLastRefreshTime] = useState(null);  // Track last refresh
  const [refreshNotification, setRefreshNotification] = useState(false);  // Show refresh notification
  const [supportQueries, setSupportQueries] = useState([]);  // Support chat queries
  const [allChatSessions, setAllChatSessions] = useState([]);  // All chat sessions (active + resolved)
  const [learningQueue, setLearningQueue] = useState([]);  // AI learning queue
  const [selectedTranscript, setSelectedTranscript] = useState(null);  // For viewing chat transcripts
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [showLearningModal, setShowLearningModal] = useState(false);
  const [selectedLearningItem, setSelectedLearningItem] = useState(null);
  const [adminAnswer, setAdminAnswer] = useState('');
  const [supportFilter, setSupportFilter] = useState('all');  // Filter: all, resolved, pending
  const [knowledgeBase, setKnowledgeBase] = useState([]);  // AI Training Q&A pairs
  const [showAddQAModal, setShowAddQAModal] = useState(false);
  const [editingQA, setEditingQA] = useState(null);
  const [newQA, setNewQA] = useState({ question: '', answer: '', category: 'General', keywords: '' });
  const [dailyStats, setDailyStats] = useState(null);  // Daily submission stats
  const [dailyStatsLoading, setDailyStatsLoading] = useState(false);
  const [dailyStatsDays, setDailyStatsDays] = useState(30);  // Number of days to show

  // Blog Management State
  const [blogArticles, setBlogArticles] = useState([]);
  const [blogTopics, setBlogTopics] = useState([]);
  const [blogStats, setBlogStats] = useState(null);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogGenerating, setBlogGenerating] = useState(false);
  const [blogMessage, setBlogMessage] = useState({ text: '', type: '' });
  const [blogFilter, setBlogFilter] = useState('all'); // all, published, draft, unpublished
  const [blogView, setBlogView] = useState('articles'); // articles, topics
  const [editingArticle, setEditingArticle] = useState(null);
  const [showBlogEditModal, setShowBlogEditModal] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/login?password=${encodeURIComponent(password)}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        setAdminPassword(password);
        fetchSubmissions(password);
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // BLOG MANAGEMENT FUNCTIONS
  // ============================================
  const fetchBlogData = async () => {
    setBlogLoading(true);
    const backendUrl = getBackendUrl();
    try {
      const [articlesRes, topicsRes, statsRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/blog/articles?password=${encodeURIComponent(adminPassword)}`),
        fetch(`${backendUrl}/api/admin/blog/topics?password=${encodeURIComponent(adminPassword)}`),
        fetch(`${backendUrl}/api/admin/blog/stats?password=${encodeURIComponent(adminPassword)}`)
      ]);
      if (articlesRes.ok) {
        const ad = await articlesRes.json();
        setBlogArticles(ad.articles || []);
      }
      if (topicsRes.ok) {
        const td = await topicsRes.json();
        setBlogTopics(td.topics || []);
      }
      if (statsRes.ok) {
        const sd = await statsRes.json();
        setBlogStats(sd);
      }
    } catch (err) {
      setBlogMessage({ text: 'Failed to fetch blog data', type: 'error' });
    } finally {
      setBlogLoading(false);
    }
  };

  const handleGenerateArticle = async () => {
    setBlogGenerating(true);
    setBlogMessage({ text: '', type: '' });
    const backendUrl = getBackendUrl();
    try {
      const res = await fetch(`${backendUrl}/api/admin/blog/generate?password=${encodeURIComponent(adminPassword)}`, { method: 'POST' });
      const data = await res.json();
      if (data.status === 'success') {
        setBlogMessage({ text: `Generated: ${data.article?.title || 'New Article'}`, type: 'success' });
        fetchBlogData();
      } else if (data.status === 'no_topics') {
        setBlogMessage({ text: 'No pending topics left in the queue', type: 'warning' });
      } else {
        setBlogMessage({ text: data.message || 'Generation failed', type: 'error' });
      }
    } catch (err) {
      setBlogMessage({ text: 'Failed to generate article', type: 'error' });
    } finally {
      setBlogGenerating(false);
    }
  };

  const handleUpdateArticleStatus = async (slug, newStatus) => {
    const backendUrl = getBackendUrl();
    try {
      const res = await fetch(`${backendUrl}/api/admin/blog/articles/${slug}?password=${encodeURIComponent(adminPassword)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setBlogMessage({ text: `Article ${newStatus}`, type: 'success' });
        fetchBlogData();
      }
    } catch (err) {
      setBlogMessage({ text: 'Failed to update article', type: 'error' });
    }
  };

  const handleDeleteArticle = async (slug) => {
    if (!window.confirm('Delete this article permanently?')) return;
    const backendUrl = getBackendUrl();
    try {
      const res = await fetch(`${backendUrl}/api/admin/blog/articles/${slug}?password=${encodeURIComponent(adminPassword)}`, { method: 'DELETE' });
      if (res.ok) {
        setBlogMessage({ text: 'Article deleted', type: 'success' });
        fetchBlogData();
      }
    } catch (err) {
      setBlogMessage({ text: 'Failed to delete article', type: 'error' });
    }
  };

  const handleSaveArticleEdit = async () => {
    if (!editingArticle) return;
    const backendUrl = getBackendUrl();
    try {
      const res = await fetch(`${backendUrl}/api/admin/blog/articles/${editingArticle.slug}?password=${encodeURIComponent(adminPassword)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingArticle.title,
          excerpt: editingArticle.excerpt,
          category: editingArticle.category,
          meta_description: editingArticle.meta_description,
          status: editingArticle.status,
        })
      });
      if (res.ok) {
        setBlogMessage({ text: 'Article saved', type: 'success' });
        setShowBlogEditModal(false);
        setEditingArticle(null);
        fetchBlogData();
      }
    } catch (err) {
      setBlogMessage({ text: 'Failed to save', type: 'error' });
    }
  };

  // Universal refresh function based on active tab
  const handleRefresh = () => {
    switch (activeTab) {
      case 'submissions':
        fetchSubmissions(adminPassword, dateFilter.startDate, dateFilter.endDate, statusFilter, currentPage);
        break;
      case 'analytics':
        fetchAnalytics(adminPassword);
        break;
      case 'settings':
        fetchPurgeStats();
        break;
      case 'support':
        fetchSupportQueries(adminPassword);
        break;
      case 'allchats':
        fetchAllChatSessions();
        break;
      case 'learning':
        fetchLearningQueue('pending');  // Default to pending items
        break;
      case 'training':
        fetchKnowledgeBase(adminPassword);
        break;
      case 'blog':
        fetchBlogData();
        break;
      default:
        fetchSubmissions(adminPassword);
    }
  };

  const fetchSubmissions = async (pwd, startDate = '', endDate = '', status = '', page = 1) => {
    setLoading(true);
    setError('');
    try {
      // Use relative URL for production or env variable for development
      const backendUrl = getBackendUrl();
      
      let url = `${backendUrl}/api/admin/submissions?password=${encodeURIComponent(pwd)}&page=${page}&limit=50`;  // Increased to 50 per page
      
      // Add date filters if provided
      if (startDate) {
        url += `&start_date=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&end_date=${encodeURIComponent(endDate)}`;
      }
      
      // Add status filter if provided
      if (status) {
        url += `&status=${encodeURIComponent(status)}`;
      }
      
      // Add timeout to fetch request - increased to 60 seconds for production
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      const response = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSubmissions(data.submissions);
        setPagination(data.pagination);
        setCurrentPage(page);
        calculateDuplicates(data.submissions);
        
        // Apply duplicate filter if enabled
        const finalSubmissions = hideDuplicates ? removeDuplicates(data.submissions) : data.submissions;
        setFilteredSubmissions(finalSubmissions);
        
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.message || 'Failed to load submissions');
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
      if (err.name === 'AbortError') {
        setError('Request timeout. Please try using date filters to narrow your search.');
      } else {
        setError(`Failed to load submissions: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };


  // WebSocket Connection
  useEffect(() => {
    if (!isAuthenticated) return;

    const connectWebSocket = () => {
      const backendUrl = getBackendUrl();
      const wsUrl = backendUrl.replace('http://', 'ws://').replace('https://', 'wss://');
      
      console.log('🔌 Connecting to WebSocket:', `${wsUrl}/ws/admin`);
      
      const ws = new WebSocket(`${wsUrl}/ws/admin`);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected');
        setWsConnected(true);
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        
        // Store interval ID to clean up later
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket message:', data);
          
          switch (data.type) {
            case 'connected':
              console.log('✅ Connection confirmed:', data.message);
              break;
            
            case 'new_submission':
              handleNewSubmission(data.submission);
              break;
            
            case 'status_update':
              handleStatusUpdate(data);
              break;
            
            case 'pong':
              // Pong response to keep-alive ping
              break;
            
            default:
              console.log('Unknown message type:', data.type);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setWsConnected(false);
      };
      
      ws.onclose = () => {
        console.log('❌ WebSocket disconnected');
        setWsConnected(false);
        
        // Clear ping interval
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          console.log('🔄 Attempting to reconnect...');
          connectWebSocket();
        }, 5000);
      };
      
      setWsInstance(ws);
    };
    
    connectWebSocket();
    
    // Cleanup on unmount
    return () => {
      if (wsInstance) {
        if (wsInstance.pingInterval) {
          clearInterval(wsInstance.pingInterval);
        }
        wsInstance.close();
      }
    };
  }, [isAuthenticated]);

  // Polling fallback when WebSocket fails (every 30 seconds)
  useEffect(() => {
    if (!isAuthenticated) return;
    if (wsConnected) {
      setAutoRefreshActive(true);
      return; // WebSocket is working, no need for polling
    }

    // If WebSocket is not connected, enable auto-refresh via polling
    setAutoRefreshActive(true);
    
    const pollingInterval = setInterval(() => {
      // Only auto-refresh if:
      // 1. Not showing any modal
      // 2. Not actively searching (has search term)
      // 3. Not using a status filter
      // 4. Not viewing a specific submission
      // 5. Auto-refresh is enabled
      const hasActiveSearch = searchTerm && searchTerm.trim().length > 0;
      const hasActiveFilter = statusFilter && statusFilter.length > 0;
      
      if (!selectedSubmission && !showUpdateModal && !showBulkModal && !hasActiveSearch && !hasActiveFilter && autoRefreshEnabled) {
        console.log('🔄 Polling: Auto-refreshing submissions...');
        fetchSubmissions(adminPassword);
      } else if (hasActiveSearch) {
        console.log('⏸️ Polling paused: Active search detected');
      } else if (hasActiveFilter) {
        console.log('⏸️ Polling paused: Status filter active');
      }
    }, 30000); // Poll every 30 seconds

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isAuthenticated, wsConnected, selectedSubmission, showUpdateModal, showBulkModal, searchTerm, statusFilter, autoRefreshEnabled, adminPassword]);

  // Handle new submission from WebSocket
  const handleNewSubmission = (submission) => {
    console.log('🆕 New submission received:', submission);
    
    // Add to submissions list at the top
    setSubmissions(prev => [submission, ...prev]);
    
    // Show notification
    setNotificationData({
      type: 'new_submission',
      title: 'New Submission Received',
      message: `${submission.customer_name} - ${submission.cards[0]?.brand} $${submission.cards[0]?.value}`,
      reference: submission.reference_number
    });
    setShowNotification(true);
    
    // Play notification sound (optional)
    playNotificationSound();
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => setShowNotification(false), 5000);
  };

  // Handle status update from WebSocket
  const handleStatusUpdate = (data) => {
    console.log('🔄 Status update received:', data);
    
    // Update submission in list
    setSubmissions(prev => prev.map(sub => 
      sub.reference_number === data.reference_number
        ? { ...sub, status: data.new_status }
        : sub
    ));
    
    // Show notification
    setNotificationData({
      type: 'status_update',
      title: 'Status Updated',
      message: `${data.reference_number}: ${data.old_status} → ${data.new_status}`,
      reference: data.reference_number
    });
    setShowNotification(true);
    
    // Auto-hide notification after 3 seconds
    setTimeout(() => setShowNotification(false), 3000);
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKvi8LhjHQU5k9nyzn0uBSF1xe/glEILElyx6OqnVBMJRp/h88BwJAUsgszz2YY2BxpqvfDinE0MElCr4+62ZB0FO5TY8tB+LgUgdMXv4JNCCxJbsOjpp1MTCUaf4fPAcSQFLILM89mGNgcaa73w4pxNDBJQq+Putmcd');
      audio.volume = 0.3;
      audio.play().catch(err => console.log('Sound play failed:', err));
    } catch (err) {
      console.log('Sound not supported');
    }
  };


  const handleSearch = async (term) => {
    setSearchTerm(term);
    
    // If search term is empty, reload default submissions
    if (!term || term.trim() === '') {
      fetchSubmissions(adminPassword, dateFilter.startDate, dateFilter.endDate);
      return;
    }
    
    // If search term is less than 2 characters, don't search (performance)
    if (term.trim().length < 2) {
      return;
    }
    
    // Perform backend search for terms 2+ characters
    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const url = `${backendUrl}/api/admin/submissions?password=${encodeURIComponent(adminPassword)}&search=${encodeURIComponent(term.trim())}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSubmissions(data.submissions);
        calculateDuplicates(data.submissions);
        const finalSubmissions = hideDuplicates ? removeDuplicates(data.submissions) : data.submissions;
        setFilteredSubmissions(finalSubmissions);
      } else {
        setError(data.message || 'Search failed');
      }
    } catch (err) {
      console.error('Error searching submissions:', err);
      setError(`Search failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (referenceNumber, newStatus, customerMessage = '') => {
    setUpdateLoading(true);
    setError('');
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: adminPassword,
          reference_number: referenceNumber,
          new_status: newStatus,
          customer_message: customerMessage
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh submissions
        await fetchSubmissions(adminPassword);
        setShowUpdateModal(false);
        setStatusUpdate({ ref: '', status: '', message: '' });
        alert(`Status updated to '${newStatus}' successfully!`);
      } else {
        setError(data.message);
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      setError('Failed to update status');
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordChangeMessage({ type: '', text: '' });
    
    if (passwordChange.new !== passwordChange.confirm) {
      setPasswordChangeMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    if (passwordChange.new.length < 8) {
      setPasswordChangeMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }
    
    setLoading(true);
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: passwordChange.current,
          new_password: passwordChange.new,
          confirm_password: passwordChange.confirm
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPasswordChangeMessage({ type: 'success', text: 'Password changed successfully!' });
        setPasswordChange({ current: '', new: '', confirm: '' });
        // Update the stored admin password
        setAdminPassword(passwordChange.new);
      } else {
        setPasswordChangeMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setPasswordChangeMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPurgeStats = async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/stats?password=${encodeURIComponent(adminPassword)}`);
      const data = await response.json();
      
      if (data.success) {
        setPurgeStats(data.stats);
        setNewPurgeDays(data.stats.auto_purge_days || 30);
      }
    } catch (err) {
      console.error('Error fetching purge stats:', err);
    }
  };

  const handleUpdatePurgeSettings = async (e) => {
    e.preventDefault();
    setPurgeMessage({ type: '', text: '' });
    
    if (newPurgeDays < 7 || newPurgeDays > 365) {
      setPurgeMessage({ type: 'error', text: 'Purge period must be between 7 and 365 days' });
      return;
    }
    
    setLoading(true);
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/update-purge-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: adminPassword,
          purge_days: parseInt(newPurgeDays)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPurgeMessage({ type: 'success', text: data.message });
        fetchPurgeStats(); // Refresh stats
      } else {
        setPurgeMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setPurgeMessage({ type: 'error', text: 'Failed to update settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleManualPurge = async () => {
    if (!window.confirm(`Are you sure you want to delete all submissions older than ${purgeStats?.auto_purge_days || 30} days? This action cannot be undone.`)) {
      return;
    }
    
    setPurgeMessage({ type: '', text: '' });
    setLoading(true);
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/manual-purge?password=${encodeURIComponent(adminPassword)}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPurgeMessage({ 
          type: 'success', 
          text: `Successfully deleted ${data.result.deleted || 0} old submissions` 
        });
        fetchPurgeStats(); // Refresh stats
        fetchSubmissions(adminPassword); // Refresh submissions list
      } else {
        setPurgeMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setPurgeMessage({ type: 'error', text: 'Failed to purge submissions. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    setCurrentPage(1); // Reset to page 1 when applying filters
    fetchSubmissions(adminPassword, dateFilter.startDate, dateFilter.endDate, statusFilter, 1);
  };

  const clearDateFilter = () => {
    setDateFilter({ startDate: '', endDate: '' });
    setCurrentPage(1);
    fetchSubmissions(adminPassword, '', '', statusFilter, 1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    fetchSubmissions(adminPassword, dateFilter.startDate, dateFilter.endDate, status, 1);
  };

  const clearAllFilters = () => {
    setDateFilter({ startDate: '', endDate: '' });
    setStatusFilter('');
    setCurrentPage(1);
    fetchSubmissions(adminPassword, '', '', '', 1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && pagination && newPage <= pagination.total_pages) {
      setCurrentPage(newPage);  // Update current page first
      fetchSubmissions(adminPassword, dateFilter.startDate, dateFilter.endDate, statusFilter, newPage);
    }
  };

  // Fetch images for a specific submission (on-demand loading)
  const fetchSubmissionImages = async (referenceNumber) => {
    if (!referenceNumber || !adminPassword) return;
    
    setLoadingImages(true);
    setImageError(null);
    
    try {
      // Use relative URL for production or env variable for development
      const backendUrl = getBackendUrl();
      
      const response = await fetch(
        `${backendUrl}/api/admin/submission/${referenceNumber}/images?password=${encodeURIComponent(adminPassword)}`,
        { timeout: 30000 } // 30 second timeout for mobile
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }
      
      const data = await response.json();
      
      if (data.success && data.cards) {
        // Update the selected submission with images
        setSelectedSubmission(prev => {
          if (!prev) return prev;
          
          return {
            ...prev,
            cards: prev.cards.map((card, idx) => ({
              ...card,
              frontImage: data.cards[idx]?.frontImage || null,
              backImage: data.cards[idx]?.backImage || null,
              receiptImage: data.cards[idx]?.receiptImage || null
            }))
          };
        });
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      setImageError('Failed to load images. Please try again.');
    } finally {
      setLoadingImages(false);
    }
  };

  // Handle submission selection and load images
  const handleViewSubmission = (submission) => {
    setSelectedSubmission(submission);
    // Fetch images after modal opens
    fetchSubmissionImages(submission.reference_number);
  };

  const handleBulkRejectPreview = async () => {
    setBulkRejectMessage({ type: '', text: '' });
    setLoading(true);
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/bulk-reject-under-50?password=${encodeURIComponent(adminPassword)}&preview_only=true`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBulkRejectPreview(data);
      } else {
        setBulkRejectMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setBulkRejectMessage({ type: 'error', text: 'Failed to load preview' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerHistory = async (email) => {
    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const url = `${backendUrl}/api/admin/submissions?password=${encodeURIComponent(adminPassword)}&search=${encodeURIComponent(email)}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setCustomerHistory({
          email: email,
          submissions: data.submissions
        });
        setShowHistoryModal(true);
      }
    } catch (err) {
      console.error('Error fetching customer history:', err);
    } finally {
      setLoading(false);
    }
  };

  const addNote = async (referenceNumber) => {
    const noteText = notesState[referenceNumber];
    if (!noteText || noteText.trim() === '') {
      alert('Please enter a note');
      return;
    }

    setNotesLoading(prev => ({ ...prev, [referenceNumber]: true }));
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/add-note`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: adminPassword,
          reference_number: referenceNumber,
          note_text: noteText
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Clear the note input
        setNotesState(prev => ({ ...prev, [referenceNumber]: '' }));
        
        // Refresh submissions to get updated notes
        fetchSubmissions(adminPassword, currentPage);
        
        alert('Note added successfully');
      } else {
        alert(`Failed to add note: ${data.message}`);
      }
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Failed to add note');
    } finally {
      setNotesLoading(prev => ({ ...prev, [referenceNumber]: false }));
    }
  };

  // Auto-refresh logic
  useEffect(() => {
    if (!isAuthenticated || !autoRefreshEnabled || !adminPassword) return;

    // Check if dashboard is "active" (no modals open, not typing)
    const isDashboardActive = () => {
      return !selectedSubmission && 
             !showUpdateModal && 
             !showBulkModal && 
             !showHistoryModal &&
             activeTab === 'submissions';
    };

    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      if (isDashboardActive() && !loading && document.visibilityState === 'visible') {
        console.log('🔄 Auto-refreshing dashboard...');
        fetchSubmissions(adminPassword, dateFilter.startDate, dateFilter.endDate, statusFilter, currentPage)
          .then(() => {
            setLastRefreshTime(new Date());
            setRefreshNotification(true);
            setTimeout(() => setRefreshNotification(false), 3000);
          })
          .catch(err => console.error('Auto-refresh error:', err));
      }
    }, autoRefreshInterval * 1000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, autoRefreshEnabled, autoRefreshInterval, selectedSubmission, showUpdateModal, 
      showBulkModal, showHistoryModal, activeTab, adminPassword, dateFilter.startDate, dateFilter.endDate, 
      statusFilter, currentPage, loading, fetchSubmissions]);

  const handleBulkRejectExecute = async () => {
    if (!bulkRejectPreview || bulkRejectPreview.summary.to_reject === 0) {
      return;
    }
    
    if (!window.confirm(`Are you sure you want to reject ${bulkRejectPreview.summary.to_reject} submissions where all cards are under $50? This action will update their status to "rejected".`)) {
      return;
    }
    
    setBulkRejectMessage({ type: '', text: '' });
    setLoading(true);
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/bulk-reject-under-50?password=${encodeURIComponent(adminPassword)}&preview_only=false`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setBulkRejectMessage({ 
          type: 'success', 
          text: `Successfully rejected ${data.summary.rejected} submissions. ${data.summary.kept} submissions kept (have cards >= $50).` 
        });
        setBulkRejectPreview(null);
        fetchSubmissions(adminPassword); // Refresh the list
      } else {
        setBulkRejectMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setBulkRejectMessage({ type: 'error', text: 'Failed to execute bulk rejection' });
    } finally {
      setLoading(false);
    }
  };

  const toggleSubmissionSelection = (referenceNumber) => {
    setSelectedSubmissions(prev => {
      if (prev.includes(referenceNumber)) {
        return prev.filter(ref => ref !== referenceNumber);
      } else {
        return [...prev, referenceNumber];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedSubmissions.length === filteredSubmissions.length) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(filteredSubmissions.map(sub => sub.reference_number));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedSubmissions.length === 0) {
      alert('Please select at least one submission');
      return;
    }

    if (!bulkAction.status) {
      alert('Please select a status');
      return;
    }

    setUpdateLoading(true);

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/bulk-update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: adminPassword,
          reference_numbers: selectedSubmissions,
          new_status: bulkAction.status,
          customer_message: bulkAction.message
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`Successfully updated ${data.updated_count} submission(s)!`);
        setSelectedSubmissions([]);
        setBulkAction({ status: '', message: '' });
        setShowBulkModal(false);
        await fetchSubmissions(adminPassword);
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (err) {
      alert('Failed to update submissions. Please try again.');
    } finally {
      setUpdateLoading(false);
    }
  };

  // Quick Action: Approve
  const quickApprove = async (referenceNumber) => {
    if (confirm('Quick approve this submission?')) {
      await updateStatus(referenceNumber, 'approved', 'Your submission has been approved');
    }
  };

  // Quick Action: Reject Unsupported
  const quickRejectUnsupported = async (referenceNumber) => {
    if (confirm('Reject as unsupported card?')) {
      await updateStatus(referenceNumber, 'rejected', 'We do not accept this card brand at this time. Thank you for your interest.');
    }
  };

  // Quick Action: Mark as Paid
  const quickMarkPaid = async (referenceNumber) => {
    if (confirm('Mark as paid?')) {
      await updateStatus(referenceNumber, 'paid', 'Payment has been processed successfully');
    }
  };

  // Delete Submission
  const deleteSubmission = async (referenceNumber) => {
    if (!confirm(`Are you sure you want to PERMANENTLY DELETE submission ${referenceNumber}? This cannot be undone.`)) {
      return;
    }
    
    setUpdateLoading(true);
    try {
      const backendUrl = getBackendUrl();
      
      // Using POST instead of DELETE for better proxy/ingress compatibility
      const url = `${backendUrl}/api/admin/submission/${referenceNumber}/delete?password=${encodeURIComponent(adminPassword)}`;
      
      const response = await fetch(url, { method: 'POST' });
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        alert('❌ Server error. Please try again later.');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        alert(`✅ ${referenceNumber} deleted successfully`);
        fetchSubmissions(adminPassword, dateFilter.startDate, dateFilter.endDate, statusFilter, currentPage);
      } else {
        alert(`❌ ${data.message || 'Failed to delete submission'}`);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert(`❌ Error: ${err.message || 'Network error'}`);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Reference', 'Customer Name', 'Email', 'Phone', 'Status', 'Submitted At', 'Card Brand', 'Card Value', 'Card Type', 'Payment Method'];
    
    const rows = filteredSubmissions.map(sub => {
      const cardDetails = sub.cards && sub.cards.length > 0 
        ? sub.cards.map(card => `${card.brand} $${card.value} (${card.cardType})`).join('; ')
        : 'N/A';
      
      return [
        sub.reference_number,
        sub.customer_name,
        sub.email,
        sub.phone || 'N/A',
        sub.status,
        sub.submitted_at,
        sub.cards && sub.cards.length > 0 ? sub.cards.map(c => c.brand).join(', ') : 'N/A',
        sub.cards && sub.cards.length > 0 ? sub.cards.reduce((sum, c) => sum + parseFloat(c.value || 0), 0) : 0,
        sub.cards && sub.cards.length > 0 ? sub.cards[0].cardType : 'N/A',
        sub.payment_method || 'N/A'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `submissions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch Analytics
  const fetchAnalytics = async (pwd) => {
    if (!pwd) return;
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/analytics?password=${encodeURIComponent(pwd)}`);
      const data = await response.json();
      
      if (data.success) {
        setAnalytics(data.analytics);
      } else {
        console.error('Analytics fetch failed:', data.message);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  // Fetch Daily Stats
  const fetchDailyStats = async (pwd, days = 30) => {
    if (!pwd) return;
    
    setDailyStatsLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/daily-stats?password=${encodeURIComponent(pwd)}&days=${days}`);
      const data = await response.json();
      
      if (data.success) {
        setDailyStats(data);
      } else {
        console.error('Daily stats fetch failed:', data.message);
      }
    } catch (err) {
      console.error('Failed to fetch daily stats:', err);
    } finally {
      setDailyStatsLoading(false);
    }
  };

  const fetchSupportQueries = async (pwd) => {
    if (!pwd) return;
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/support-queries?password=${encodeURIComponent(pwd)}&status=${supportFilter}`);
      const data = await response.json();
      
      if (data.success) {
        setSupportQueries(data.queries);
      } else {
        console.error('Support queries fetch failed:', data.message);
      }
    } catch (err) {
      console.error('Failed to fetch support queries:', err);
    }
  };

  const fetchAllChatSessions = async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/all-chat-sessions?password=${encodeURIComponent(adminPassword)}`);
      const data = await response.json();
      
      if (data.success) {
        setAllChatSessions(data.sessions || []);
      } else {
        console.error('Failed to fetch all chat sessions');
      }
    } catch (error) {
      console.error('Error fetching all chat sessions:', error);
    }
  };

  // Fetch chat transcript for a specific session
  const fetchChatTranscript = async (sessionId) => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/chat-transcript?password=${encodeURIComponent(adminPassword)}&session_id=${encodeURIComponent(sessionId)}`);
      const data = await response.json();
      
      if (data.success) {
        return data.chat_transcript || [];
      } else {
        console.error('Failed to fetch chat transcript:', data.message);
        return [];
      }
    } catch (error) {
      console.error('Error fetching chat transcript:', error);
      return [];
    }
  };

  // Handle View Chat button click - fetch transcript first
  const handleViewChat = async (query) => {
    // Show loading state
    setSelectedTranscript({ ...query, chat_transcript: null, loading: true });
    setShowTranscriptModal(true);
    
    // Fetch the actual transcript
    const transcript = await fetchChatTranscript(query.session_id);
    setSelectedTranscript({ ...query, chat_transcript: transcript, loading: false });
  };

  const fetchLearningQueue = async (status = 'all') => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/learning-queue?password=${adminPassword}&status=${status}`);
      const data = await response.json();
      
      if (data.success) {
        setLearningQueue(data.items || []);
      } else {
        console.error('Failed to fetch learning queue');
      }
    } catch (error) {
      console.error('Error fetching learning queue:', error);
    }
  };

  const provideLearningAnswer = async (itemId, answer, addToKb = true) => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/provide-learning-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: adminPassword,
          item_id: itemId,
          admin_answer: answer,
          add_to_kb: addToKb
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`Answer provided successfully!${addToKb ? ' Added to knowledge base.' : ''}`);
        fetchLearningQueue('pending'); // Refresh the queue
        setShowLearningModal(false);
        setAdminAnswer('');
      } else {
        alert('Failed to provide answer: ' + data.message);
      }
    } catch (error) {
      console.error('Error providing learning answer:', error);
      alert('Error providing answer');
    }
  };

  const markQueryResolved = async (sessionId) => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/resolve-query?password=${encodeURIComponent(adminPassword)}&session_id=${sessionId}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        // Refresh queries
        fetchSupportQueries(adminPassword);
      }
    } catch (err) {
      console.error('Failed to resolve query:', err);
    }
  };

  const fetchKnowledgeBase = async (pwd) => {
    if (!pwd) return;
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/knowledge-base?password=${encodeURIComponent(pwd)}`);
      const data = await response.json();
      
      if (data.success) {
        setKnowledgeBase(data.qa_pairs);
      }
    } catch (err) {
      console.error('Failed to fetch knowledge base:', err);
    }
  };

  const addQAPair = async () => {
    if (!newQA.question.trim() || !newQA.answer.trim()) {
      alert('Please fill in both question and answer');
      return;
    }

    try {
      const backendUrl = getBackendUrl();
      const keywords = newQA.keywords ? newQA.keywords.split(',').map(k => k.trim()) : [];
      
      const response = await fetch(`${backendUrl}/api/admin/knowledge-base/add?password=${encodeURIComponent(adminPassword)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: newQA.question,
          answer: newQA.answer,
          category: newQA.category,
          keywords: keywords
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowAddQAModal(false);
        setNewQA({ question: '', answer: '', category: 'General', keywords: '' });
        fetchKnowledgeBase(adminPassword);
        alert('Q&A pair added successfully!');
      } else {
        alert(`Failed: ${data.message}`);
      }
    } catch (err) {
      console.error('Failed to add Q&A:', err);
      alert('Failed to add Q&A pair');
    }
  };

  const updateQAPair = async () => {
    if (!newQA.question.trim() || !newQA.answer.trim()) {
      alert('Please fill in both question and answer');
      return;
    }

    try {
      const backendUrl = getBackendUrl();
      const keywords = newQA.keywords ? newQA.keywords.split(',').map(k => k.trim()) : [];
      
      const response = await fetch(`${backendUrl}/api/admin/knowledge-base/update?password=${encodeURIComponent(adminPassword)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingQA.id,
          question: newQA.question,
          answer: newQA.answer,
          category: newQA.category,
          keywords: keywords
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowAddQAModal(false);
        setEditingQA(null);
        setNewQA({ question: '', answer: '', category: 'General', keywords: '' });
        fetchKnowledgeBase(adminPassword);
        alert('Q&A pair updated successfully!');
      } else {
        alert(`Failed: ${data.message}`);
      }
    } catch (err) {
      console.error('Failed to update Q&A:', err);
      alert('Failed to update Q&A pair');
    }
  };

  const deleteQAPair = async (id) => {
    if (!confirm('Are you sure you want to delete this Q&A pair?')) return;

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/knowledge-base/delete?password=${encodeURIComponent(adminPassword)}&id=${id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        fetchKnowledgeBase(adminPassword);
        alert('Q&A pair deleted successfully!');
      }
    } catch (err) {
      console.error('Failed to delete Q&A:', err);
    }
  };

  // Detect duplicates and calculate unique cards
  const calculateDuplicates = (subs) => {
    const cardMap = new Map();
    let totalCards = 0;
    const duplicateRefs = new Set();
    
    subs.forEach(sub => {
      if (sub.cards) {
        sub.cards.forEach(card => {
          totalCards++;
          // Create unique key based on card details
          const key = `${card.brand}-${card.value}-${card.digitalCode || ''}-${card.cardType}`;
          
          if (cardMap.has(key)) {
            // Duplicate found
            cardMap.get(key).push(sub.reference_number);
            duplicateRefs.add(sub.reference_number);
          } else {
            cardMap.set(key, [sub.reference_number]);
          }
        });
      }
    });
    
    const uniqueCards = cardMap.size;
    const duplicateCount = totalCards - uniqueCards;
    
    setDuplicateInfo({
      total: totalCards,
      unique: uniqueCards,
      duplicates: duplicateCount
    });
    
    return { cardMap, duplicateRefs };
  };

  // Filter submissions to remove duplicates
  const removeDuplicates = (subs) => {
    const seen = new Set();
    return subs.filter(sub => {
      if (!sub.cards || sub.cards.length === 0) return true;
      
      const cardKeys = sub.cards.map(card => 
        `${card.brand}-${card.value}-${card.digitalCode || ''}-${card.cardType}`
      ).join('|');
      
      if (seen.has(cardKeys)) {
        return false; // Duplicate
      }
      seen.add(cardKeys);
      return true; // Unique
    });
  };
  
  const openUpdateModal = (referenceNumber) => {
    setStatusUpdate({ ref: referenceNumber, status: '', message: '' });
    setShowUpdateModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'received': 'bg-blue-100 text-blue-800',
      'under_review': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'paid': 'bg-green-200 text-green-900',
      'rejected': 'bg-red-100 text-red-800',
      'duplicate': 'bg-orange-100 text-orange-800'
    };
    
    return styles[status] || styles['received'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        timeZone: 'America/New_York', // US Eastern Time
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }) + ' ET';
    } catch {
      return dateString;
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4">
        <PageSeo
          title="Admin Login | CashifyGCMart"
          description="Internal admin login."
          path="/admin"
          noindex={true}
        />
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-4">
                <Lock className="w-8 h-8 text-pink-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Login</h1>
              <p className="text-gray-600">CashifyGCMart Dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    required
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" style={{ opacity: 0.5 }} />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold py-3 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <PageSeo
        title="Admin Dashboard | CashifyGCMart"
        description="Internal admin dashboard."
        path="/admin"
        noindex={true}
      />
      <div className="max-w-7xl mx-auto">
        {/* Header - Enhanced for mobile */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 sticky top-0 z-40 lg:static">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Admin Dashboard</h1>
              <p className="text-xs sm:text-base text-gray-600 mt-1">Manage submissions efficiently</p>
            </div>
            
            {/* Action Buttons - Show on all tabs */}
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {/* Tab-specific buttons */}
              {activeTab === 'submissions' && (
                <>
                  <button
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 active:bg-purple-700 transition-all flex-1 sm:flex-initial min-h-[48px] font-semibold shadow-md active:scale-95"
                  >
                    <Filter className="w-5 h-5" />
                    <span className="sm:inline">Filters</span>
                  </button>
                  <button
                    onClick={exportToCSV}
                    disabled={filteredSubmissions.length === 0}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 active:bg-green-700 transition-all disabled:opacity-50 flex-1 sm:flex-initial min-h-[48px] font-semibold shadow-md active:scale-95 disabled:cursor-not-allowed"
                  >
                    <Download className="w-5 h-5" />
                    <span className="sm:inline">Export</span>
                  </button>
                </>
              )}
              
              {/* Universal Refresh Button - Show on ALL tabs */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-pink-500 text-white rounded-xl hover:bg-pink-600 active:bg-pink-700 transition-all disabled:opacity-50 flex-1 sm:flex-initial min-h-[48px] font-semibold shadow-md active:scale-95 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span className="sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {/* Auto-Refresh Notification */}
          {refreshNotification && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-800 animate-fade-in">
              <CheckCircle className="w-4 h-4" />
              <span>Dashboard refreshed automatically</span>
            </div>
          )}
          
          {/* Tab Navigation - Mobile Scrollable */}
          <div className="border-b border-gray-200 mb-6 overflow-x-auto">
            <nav className="-mb-px flex space-x-4 md:space-x-8 min-w-max md:min-w-0" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('submissions')}
              className={`pb-3 px-1 font-semibold transition-colors ${
                activeTab === 'submissions'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Submissions
            </button>
            <button
              onClick={() => {
                setActiveTab('analytics');
                if (adminPassword) {
                  fetchAnalytics(adminPassword);
                  fetchDailyStats(adminPassword, dailyStatsDays);
                }
              }}
              className={`pb-3 px-1 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'analytics'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`pb-3 px-1 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'settings'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              onClick={() => {
                setActiveTab('support');
                if (adminPassword) {
                  fetchSupportQueries(adminPassword);
                }
              }}
              className={`pb-3 px-1 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'support'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              Support Queries
            </button>
            <button
              onClick={() => {
                setActiveTab('allchats');
                if (adminPassword) {
                  fetchAllChatSessions();
                }
              }}
              className={`pb-3 px-1 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'allchats'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              All Chats
            </button>
            <button
              onClick={() => {
                setActiveTab('learning');
                if (adminPassword) {
                  fetchLearningQueue('pending');
                }
              }}
              className={`pb-3 px-1 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'learning'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Zap className="w-4 h-4" />
              AI Learning
              {learningQueue.filter(item => !item.reviewed).length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {learningQueue.filter(item => !item.reviewed).length}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setActiveTab('training');
                if (adminPassword) {
                  fetchKnowledgeBase(adminPassword);
                }
              }}
              className={`pb-3 px-1 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'training'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Zap className="w-4 h-4" />
              AI Training
            </button>
            <button
              onClick={() => {
                setActiveTab('blog');
                if (adminPassword) fetchBlogData();
              }}
              className={`pb-3 px-1 font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'blog'
                  ? 'text-pink-600 border-b-2 border-pink-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="blog-tab-button"
            >
              <TrendingUp className="w-4 h-4" />
              Blog
              {blogStats && blogStats.pending_topics > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                  {blogStats.pending_topics}
                </span>
              )}
            </button>
            </nav>
          </div>
        </div>

        {/* Submissions Tab Content */}
        {activeTab === 'submissions' && (
          <>
            {/* Search Bar and Date Filter - Enhanced for mobile */}
            <div className="bg-white rounded-xl shadow-lg p-4 mb-4 sm:mb-6">
              {/* Search Bar - Larger for mobile */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search reference, name, email..."
                  className="w-full pl-12 pr-4 py-4 text-base border-2 border-gray-300 rounded-xl focus:border-pink-500 focus:ring-4 focus:ring-pink-200 outline-none transition-all shadow-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => handleSearch('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {/* Filter Controls - Mobile optimized */}
              <div className="space-y-3">
                {/* Status Filter */}
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                  <div className="flex-1 w-full">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Filter by Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilter(e.target.value)}
                      disabled={loading}
                      className="w-full px-4 py-4 text-base border-2 border-gray-300 rounded-xl focus:border-pink-500 focus:ring-4 focus:ring-pink-200 outline-none disabled:opacity-50 bg-white font-semibold appearance-none cursor-pointer"
                      style={{
                        backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                        backgroundPosition: 'right 0.5rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.5em 1.5em',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="">📋 All Statuses</option>
                      <option value="received">📥 Received</option>
                      <option value="under_review">🔍 Under Review</option>
                      <option value="approved">✅ Approved</option>
                      <option value="paid">💰 Paid</option>
                      <option value="rejected">❌ Rejected</option>
                      <option value="duplicate">🔄 Duplicate</option>
                    </select>
                  </div>

                  {/* Date Range Filter */}
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={dateFilter.startDate}
                      onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={dateFilter.endDate}
                      onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleDateFilter}
                      disabled={loading}
                      className="px-6 py-2 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      Apply Dates
                    </button>
                    
                    {(dateFilter.startDate || dateFilter.endDate || statusFilter) && (
                      <button
                        onClick={clearAllFilters}
                        disabled={loading}
                        className="px-6 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="bg-white rounded-lg shadow-md p-4 mb-6 border-2 border-purple-200">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Advanced Filters</h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Card Brand
                    </label>
                    <select
                      value={advancedFilters.cardBrand}
                      onChange={(e) => setAdvancedFilters({...advancedFilters, cardBrand: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                    >
                      <option value="">All Brands</option>
                      <option value="Amazon">Amazon</option>
                      <option value="Target">Target</option>
                      <option value="Walmart">Walmart</option>
                      <option value="Best Buy">Best Buy</option>
                      <option value="Starbucks">Starbucks</option>
                      <option value="iTunes">iTunes</option>
                      <option value="Google Play">Google Play</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Min Value ($)
                    </label>
                    <input
                      type="number"
                      value={advancedFilters.minValue}
                      onChange={(e) => setAdvancedFilters({...advancedFilters, minValue: e.target.value})}
                      placeholder="0"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Max Value ($)
                    </label>
                    <input
                      type="number"
                      value={advancedFilters.maxValue}
                      onChange={(e) => setAdvancedFilters({...advancedFilters, maxValue: e.target.value})}
                      placeholder="1000"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Has Receipt
                    </label>
                    <select
                      value={advancedFilters.hasReceipt}
                      onChange={(e) => setAdvancedFilters({...advancedFilters, hasReceipt: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                    >
                      <option value="">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      // Apply client-side filters
                      let filtered = submissions;
                      
                      if (advancedFilters.cardBrand) {
                        filtered = filtered.filter(sub => 
                          sub.cards && sub.cards.some(card => card.brand === advancedFilters.cardBrand)
                        );
                      }
                      
                      if (advancedFilters.minValue) {
                        filtered = filtered.filter(sub => 
                          sub.cards && sub.cards.some(card => parseFloat(card.value) >= parseFloat(advancedFilters.minValue))
                        );
                      }
                      
                      if (advancedFilters.maxValue) {
                        filtered = filtered.filter(sub => 
                          sub.cards && sub.cards.some(card => parseFloat(card.value) <= parseFloat(advancedFilters.maxValue))
                        );
                      }
                      
                      if (advancedFilters.hasReceipt) {
                        filtered = filtered.filter(sub => 
                          sub.cards && sub.cards.some(card => card.hasReceipt === advancedFilters.hasReceipt)
                        );
                      }
                      
                      setFilteredSubmissions(filtered);
                    }}
                    className="px-6 py-2 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    Apply Filters
                  </button>
                  
                  <button
                    onClick={() => {
                      setAdvancedFilters({ cardBrand: '', minValue: '', maxValue: '', hasReceipt: '', fraudScore: '' });
                      setFilteredSubmissions(submissions);
                    }}
                    className="px-6 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}

            {/* Bulk Actions Panel */}
            {selectedSubmissions.length > 0 && (
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg shadow-lg p-4 mb-6 text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span className="font-semibold text-lg">
                      {selectedSubmissions.length} submission{selectedSubmissions.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowBulkModal(true)}
                      className="px-6 py-2 bg-white text-pink-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Bulk Update Status
                    </button>
                    <button
                      onClick={() => setSelectedSubmissions([])}
                      className="px-6 py-2 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-pink-600 transition-colors"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Today's Submissions */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-semibold">Today's Submissions</p>
                    <p className="text-3xl font-bold mt-1">
                      {filteredSubmissions.filter(s => {
                        const today = new Date().toISOString().split('T')[0];
                        return s.submitted_at && s.submitted_at.startsWith(today);
                      }).length}
                    </p>
                  </div>
                  <svg className="w-10 h-10 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

            {/* WebSocket Status & Notification */}
            <div className="mb-6 flex items-center justify-between">
              {/* WebSocket Connection Indicator */}
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${wsConnected ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <span className="text-sm font-medium">
                  {(wsConnected || autoRefreshActive) ? '🔴 LIVE - Auto-updating' : '⚪ Offline - Manual refresh required'}
                </span>
              </div>

              {/* Live Notification Popup */}
              {showNotification && notificationData && (
                <div className="fixed top-20 right-4 z-50 animate-slide-in-right">
                  <div className="bg-white rounded-lg shadow-2xl border-l-4 border-blue-500 p-4 min-w-[300px] max-w-[400px]">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {notificationData.type === 'new_submission' ? (
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <DollarSign className="w-5 h-5 text-blue-600" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                          )}
                          <h3 className="font-semibold text-gray-900">{notificationData.title}</h3>
                        </div>
                        <p className="text-sm text-gray-600 ml-10">{notificationData.message}</p>
                        {notificationData.reference && (
                          <p className="text-xs text-gray-400 ml-10 mt-1">Ref: {notificationData.reference}</p>
                        )}
                      </div>
                      <button
                        onClick={() => setShowNotification(false)}
                        className="text-gray-400 hover:text-gray-600 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>


              {/* Total Value */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-semibold">Total Value</p>
                    <p className="text-3xl font-bold mt-1">
                      ${filteredSubmissions.reduce((sum, sub) => {
                        const subTotal = (sub.cards || []).reduce((cardSum, card) => {
                          return cardSum + (parseFloat(card.value) || 0);
                        }, 0);
                        return sum + subTotal;
                      }, 0).toLocaleString()}
                    </p>
                  </div>
                  <svg className="w-10 h-10 text-green-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              {/* Pending Review */}
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm font-semibold">Pending Review</p>
                    <p className="text-3xl font-bold mt-1">
                      {filteredSubmissions.filter(s => s.status === 'received' || s.status === 'under_review').length}
                    </p>
                  </div>
                  <svg className="w-10 h-10 text-orange-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>

              {/* Total Submissions */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-semibold">Total Showing</p>
                    <p className="text-3xl font-bold mt-1">{filteredSubmissions.length}</p>
                  </div>
                  <svg className="w-10 h-10 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

        {/* Duplicate Detection Info */}
        {duplicateInfo.total > 0 && (
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg shadow-md p-4 mb-6 text-white">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white bg-opacity-20 rounded-lg p-3">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm font-semibold opacity-90">Card Statistics</p>
                  <div className="flex gap-4 mt-1">
                    <div>
                      <span className="text-lg font-bold">{duplicateInfo.total}</span>
                      <span className="text-sm opacity-80 ml-1">Total Cards</span>
                    </div>
                    <div className="border-l border-white border-opacity-30 pl-4">
                      <span className="text-lg font-bold">{duplicateInfo.unique}</span>
                      <span className="text-sm opacity-80 ml-1">Unique Cards</span>
                    </div>
                    {duplicateInfo.duplicates > 0 && (
                      <div className="border-l border-white border-opacity-30 pl-4">
                        <span className="text-lg font-bold">{duplicateInfo.duplicates}</span>
                        <span className="text-sm opacity-80 ml-1">Duplicates</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {duplicateInfo.duplicates > 0 && (
                <label className="flex items-center gap-2 cursor-pointer bg-white bg-opacity-20 hover:bg-opacity-30 transition-all px-4 py-2 rounded-lg">
                  <input
                    type="checkbox"
                    checked={hideDuplicates}
                    onChange={(e) => {
                      setHideDuplicates(e.target.checked);
                      const finalSubs = e.target.checked ? removeDuplicates(submissions) : submissions;
                      setFilteredSubmissions(finalSubs);
                    }}
                    className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm font-semibold">Hide Duplicates</span>
                </label>
              )}
            </div>
          </div>
        )}

        {/* Status Breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {['received', 'under_review', 'approved', 'paid', 'rejected', 'duplicate'].map((status) => {
            const count = filteredSubmissions.filter(s => s.status === status).length;
            const colors = {
              received: 'border-blue-300 bg-blue-50 text-blue-700',
              under_review: 'border-yellow-300 bg-yellow-50 text-yellow-700',
              approved: 'border-green-300 bg-green-50 text-green-700',
              paid: 'border-purple-300 bg-purple-50 text-purple-700',
              rejected: 'border-red-300 bg-red-50 text-red-700'
            };
            return (
              <div key={status} className={`border-2 rounded-lg p-3 ${colors[status]}`}>
                <p className="text-xs font-semibold capitalize">{status.replace('_', ' ')}</p>
                <p className="text-xl font-bold mt-1">{count}</p>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800">
            {error}
          </div>
        )}

        {/* Info message when showing limited results */}
        {!dateFilter.startDate && !dateFilter.endDate && filteredSubmissions.length >= 50 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-blue-800">
            <p className="font-semibold">ℹ️ Showing most recent 50 submissions per page</p>
            <p className="text-sm mt-1">Use the date range filters above to view more submissions or narrow your search.</p>
          </div>
        )}

        {/* Submissions Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Desktop Table View - Hidden on Mobile */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedSubmissions.length === filteredSubmissions.length && filteredSubmissions.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cards</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSubmissions.map((sub) => (
                  <tr key={sub.reference_number} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedSubmissions.includes(sub.reference_number)}
                        onChange={() => toggleSubmissionSelection(sub.reference_number)}
                        className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
                      />
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {sub.reference_number}
                        {sub.is_duplicate && sub.duplicate_of && (
                          <span 
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 cursor-help" 
                            title={`Duplicate of ${sub.duplicate_of}`}
                          >
                            🔄 DUP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <div>{sub.customer_name}</div>
                      <button
                        onClick={() => fetchCustomerHistory(sub.email)}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                        title="View customer history"
                      >
                        {sub.email}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {sub.cards && sub.cards.map((card, idx) => (
                        <div key={idx} className="text-xs">
                          {card.brand} ${card.value}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {formatDate(sub.submitted_at)}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(sub.status)}`}>
                        {sub.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-1 flex-wrap">
                        <button
                          onClick={() => handleViewSubmission(sub)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {sub.status === 'received' && (
                          <button
                            onClick={() => quickApprove(sub.reference_number)}
                            disabled={updateLoading}
                            className="px-2 py-1 text-xs font-semibold bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                            title="Quick Approve"
                          >
                            <Zap className="w-3 h-3" />
                            Approve
                          </button>
                        )}
                        
                        {(sub.status === 'received' || sub.status === 'under_review') && (
                          <button
                            onClick={() => quickRejectUnsupported(sub.reference_number)}
                            disabled={updateLoading}
                            className="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center gap-1"
                            title="Quick Reject - Unsupported"
                          >
                            <XCircle className="w-3 h-3" />
                            Reject
                          </button>
                        )}
                        
                        {sub.status === 'approved' && (
                          <button
                            onClick={() => quickMarkPaid(sub.reference_number)}
                            disabled={updateLoading}
                            className="px-2 py-1 text-xs font-semibold bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1"
                            title="Mark as Paid"
                          >
                            <DollarSign className="w-3 h-3" />
                            Paid
                          </button>
                        )}
                        
                        <button
                          onClick={() => openUpdateModal(sub.reference_number)}
                          disabled={updateLoading}
                          className="px-2 py-1 text-xs font-semibold bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50"
                        >
                          Custom
                        </button>
                        
                        <button
                          onClick={() => deleteSubmission(sub.reference_number)}
                          disabled={updateLoading}
                          className="p-1 text-xs text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete Submission"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Shown on Small Screens - ENHANCED UX */}
          <div className="lg:hidden space-y-3 pb-24">
            {filteredSubmissions.map((sub) => (
              <div key={sub.reference_number} className="bg-white rounded-xl border-2 border-gray-200 shadow-md overflow-hidden active:scale-[0.98] transition-transform">
                {/* Card Header - Enhanced with larger touch areas */}
                <div className="bg-gradient-to-r from-pink-50 to-purple-50 px-4 py-4 border-b-2 border-gray-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Larger checkbox for easier tapping */}
                      <input
                        type="checkbox"
                        checked={selectedSubmissions.includes(sub.reference_number)}
                        onChange={() => toggleSubmissionSelection(sub.reference_number)}
                        className="w-5 h-5 mt-0.5 text-pink-600 rounded-md focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-bold text-gray-900 break-all">{sub.reference_number}</div>
                        {sub.is_duplicate && sub.duplicate_of && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800 mt-1">
                            🔄 Duplicate
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Larger, more prominent status badge */}
                    <span className={`inline-block px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap ${getStatusBadge(sub.status)} shadow-sm`}>
                      {sub.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Card Body - Enhanced with better spacing and readability */}
                <div className="px-4 py-4 space-y-4 bg-white">
                  {/* Customer Info - More prominent */}
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <div className="text-xs font-semibold text-blue-600 mb-2 uppercase tracking-wide">Customer</div>
                    <div className="text-base font-bold text-gray-900 mb-1">{sub.customer_name}</div>
                    <button
                      onClick={() => fetchCustomerHistory(sub.email)}
                      className="text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 flex items-center gap-2 font-medium min-h-[44px] -ml-1 pl-1"
                    >
                      <User className="w-4 h-4" />
                      {sub.email}
                    </button>
                  </div>

                  {/* Cards Info - Enhanced display */}
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <div className="text-xs font-semibold text-green-600 mb-2 uppercase tracking-wide">Gift Cards</div>
                    <div className="space-y-2">
                      {sub.cards && sub.cards.map((card, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white rounded-md px-3 py-2 shadow-sm">
                          <span className="text-sm font-semibold text-gray-900">{card.brand}</span>
                          <span className="text-base font-bold text-green-600">${card.value}</span>
                        </div>
                      ))}
                      {sub.cards && sub.cards.length > 0 && (
                        <div className="flex items-center justify-between pt-2 border-t border-green-200">
                          <span className="text-sm font-semibold text-gray-600">Total Value</span>
                          <span className="text-lg font-bold text-green-700">
                            ${sub.cards.reduce((sum, card) => sum + (parseFloat(card.value) || 0), 0).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submitted Date - More visible */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 font-medium">Submitted</span>
                    <span className="text-gray-900 font-semibold">{formatDate(sub.submitted_at)}</span>
                  </div>
                </div>

                {/* Card Actions - ENHANCED with larger touch targets */}
                <div className="bg-gradient-to-b from-gray-50 to-gray-100 px-4 py-4 border-t-2 border-gray-200">
                  {/* Primary action buttons with minimum 48px height for easy tapping */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {sub.status === 'received' && (
                      <>
                        <button
                          onClick={() => quickApprove(sub.reference_number)}
                          disabled={updateLoading}
                          className="px-4 py-4 text-sm font-bold bg-green-500 text-white rounded-xl hover:bg-green-600 active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all min-h-[56px]"
                        >
                          <CheckCircle className="w-5 h-5" />
                          <span>Approve</span>
                        </button>
                        <button
                          onClick={() => quickRejectUnsupported(sub.reference_number)}
                          disabled={updateLoading}
                          className="px-4 py-4 text-sm font-bold bg-red-500 text-white rounded-xl hover:bg-red-600 active:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all min-h-[56px]"
                        >
                          <XCircle className="w-5 h-5" />
                          <span>Reject</span>
                        </button>
                      </>
                    )}
                    
                    {(sub.status === 'under_review' && sub.status !== 'received') && (
                      <button
                        onClick={() => quickRejectUnsupported(sub.reference_number)}
                        disabled={updateLoading}
                        className="col-span-2 px-4 py-4 text-sm font-bold bg-red-500 text-white rounded-xl hover:bg-red-600 active:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all min-h-[56px]"
                      >
                        <XCircle className="w-5 h-5" />
                        <span>Reject Submission</span>
                      </button>
                    )}
                    
                    {sub.status === 'approved' && (
                      <button
                        onClick={() => quickMarkPaid(sub.reference_number)}
                        disabled={updateLoading}
                        className="col-span-2 px-4 py-4 text-sm font-bold bg-purple-500 text-white rounded-xl hover:bg-purple-600 active:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md active:scale-95 transition-all min-h-[56px]"
                      >
                        <DollarSign className="w-5 h-5" />
                        <span>Mark as Paid</span>
                      </button>
                    )}
                  </div>
                  
                  {/* Secondary actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleViewSubmission(sub)}
                      className="px-4 py-3 text-sm font-semibold text-blue-700 bg-blue-100 border-2 border-blue-200 rounded-xl hover:bg-blue-200 active:bg-blue-300 flex items-center justify-center gap-2 active:scale-95 transition-all min-h-[48px]"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Details</span>
                    </button>
                    <button
                      onClick={() => openUpdateModal(sub.reference_number)}
                      disabled={updateLoading}
                      className="px-4 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-95 transition-all min-h-[48px]"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Update</span>
                    </button>
                  </div>
                  
                  {/* Delete button */}
                  <div className="mt-2">
                    <button
                      onClick={() => deleteSubmission(sub.reference_number)}
                      disabled={updateLoading}
                      className="w-full px-4 py-3 text-sm font-semibold text-red-600 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100 active:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-95 transition-all min-h-[48px]"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Submission</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredSubmissions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No submissions found
            </div>
          )}

          {/* Pagination Controls */}
          {pagination && pagination.total_pages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-6">
              {/* Pagination Info */}
              <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                Showing <span className="font-semibold">{((currentPage - 1) * 50) + 1}</span> to{' '}
                <span className="font-semibold">
                  {Math.min(currentPage * 50, pagination.total)}
                </span> of{' '}
                <span className="font-semibold">{pagination.total}</span> submissions
              </div>

              {/* Page Controls */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.has_prev}
                  className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {/* First Page */}
                  {currentPage > 3 && (
                    <>
                      <button
                        onClick={() => handlePageChange(1)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        1
                      </button>
                      {currentPage > 4 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                    </>
                  )}

                  {/* Page Numbers Around Current */}
                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                    .filter(page => 
                      page === currentPage || 
                      page === currentPage - 1 || 
                      page === currentPage + 1 ||
                      (currentPage <= 2 && page <= 3) ||
                      (currentPage >= pagination.total_pages - 1 && page >= pagination.total_pages - 2)
                    )
                    .map(page => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                          page === currentPage
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white border-transparent'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))
                  }

                  {/* Last Page */}
                  {currentPage < pagination.total_pages - 2 && (
                    <>
                      {currentPage < pagination.total_pages - 3 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(pagination.total_pages)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        {pagination.total_pages}
                      </button>
                    </>
                  )}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.has_next}
                  className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Details Modal */}
        {selectedSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Submission Details</h2>
                <div className="flex gap-2">
                  {/* Download All Images Button */}
                  <button
                    onClick={() => {
                      const downloadImage = (url, filename) => {
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      };
                      
                      // Download all card images with delays to prevent blocking
                      selectedSubmission.cards?.forEach((card, idx) => {
                        if (card.frontImage) {
                          setTimeout(() => downloadImage(card.frontImage, `${selectedSubmission.reference_number}-front-${idx+1}.jpg`), idx * 300);
                        }
                        if (card.backImage) {
                          setTimeout(() => downloadImage(card.backImage, `${selectedSubmission.reference_number}-back-${idx+1}.jpg`), idx * 300 + 100);
                        }
                        if (card.receiptImage) {
                          setTimeout(() => downloadImage(card.receiptImage, `${selectedSubmission.reference_number}-receipt-${idx+1}.jpg`), idx * 300 + 200);
                        }
                      });
                    }}
                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Download size={14} />
                    Download All
                  </button>
                  <button
                    onClick={() => setSelectedSubmission(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Reference</p>
                    <p className="font-semibold">{selectedSubmission.reference_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(selectedSubmission.status)}`}>
                      {selectedSubmission.status.replace('_', ' ').toUpperCase()}
                    </span>
                    {selectedSubmission.is_duplicate && selectedSubmission.duplicate_of && (
                      <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-xs text-orange-800">
                          <span className="font-semibold">🔄 Duplicate Submission</span>
                          <br />
                          Original: <span className="font-mono text-orange-900">{selectedSubmission.duplicate_of}</span>
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Customer</p>
                    <p className="font-semibold">{selectedSubmission.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold text-sm">{selectedSubmission.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-semibold">{selectedSubmission.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Method</p>
                    <p className="font-semibold uppercase">{selectedSubmission.paymentMethod}</p>
                  </div>
                </div>

                {selectedSubmission.paypalAddress && (
                  <div>
                    <p className="text-sm text-gray-600">PayPal Address</p>
                    <p className="font-semibold">{selectedSubmission.paypalAddress}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-600 mb-2">Gift Cards</p>
                  <div className="space-y-3">
                    {selectedSubmission.cards && selectedSubmission.cards.map((card, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between mb-3">
                          <div>
                            <p className="font-bold text-lg">{card.brand}</p>
                            <p className="text-sm text-gray-600">{card.condition} • {card.cardType}</p>
                          </div>
                          <p className="text-xl font-bold text-pink-600">${card.value}</p>
                        </div>
                        
                        {/* FULL UNMASKED CARD DETAILS */}
                        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 mt-3">
                          <p className="text-xs text-red-800 font-semibold mb-2">🔒 SENSITIVE CARD INFORMATION</p>
                          <div className="space-y-2">
                            {card.digitalCode && (
                              <div>
                                <p className="text-xs text-gray-600">Card Number:</p>
                                <p className="font-mono font-bold text-red-900 bg-white px-2 py-1 rounded border border-red-300 text-base">
                                  {card.digitalCode}
                                </p>
                              </div>
                            )}
                            {card.digitalPin && (
                              <div>
                                <p className="text-xs text-gray-600">PIN / CVV:</p>
                                <p className="font-mono font-bold text-red-900 bg-white px-2 py-1 rounded border border-red-300 text-base">
                                  {card.digitalPin}
                                </p>
                              </div>
                            )}
                            {card.expiryDate && (
                              <div>
                                <p className="text-xs text-gray-600">Expiry Date:</p>
                                <p className="font-semibold text-gray-900">{card.expiryDate}</p>
                              </div>
                            )}
                            {!card.digitalCode && !card.digitalPin && (
                              <p className="text-xs text-gray-600 italic">No card details provided (images uploaded instead)</p>
                            )}
                          </div>
                        </div>
                        
                        {/* CARD IMAGES */}
                        <div className="mt-3">
                          <p className="text-xs text-gray-600 font-semibold mb-2">📸 Card Images</p>
                          
                          {/* Loading State */}
                          {loadingImages && (
                            <div className="text-center py-8 text-gray-600 bg-gray-50 rounded-lg">
                              <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-2"></div>
                              <p className="text-sm">Loading images...</p>
                            </div>
                          )}
                          
                          {/* Error State */}
                          {imageError && !loadingImages && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                              {imageError}
                              <button 
                                onClick={() => fetchSubmissionImages(selectedSubmission.reference_number)}
                                className="ml-2 text-red-800 underline hover:no-underline font-semibold"
                              >
                                Retry
                              </button>
                            </div>
                          )}
                          
                          {/* Images Grid */}
                          {!loadingImages && !imageError && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {card.frontImage && (
                              <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                                <div className="bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
                                  Card Front
                                </div>
                                <img 
                                  src={card.frontImage} 
                                  alt="Card Front" 
                                  loading="lazy"
                                  className="w-full h-auto cursor-pointer hover:opacity-90"
                                  onClick={() => window.open(card.frontImage, '_blank')}
                                />
                                <div className="flex">
                                  <button
                                    onClick={() => window.open(card.frontImage, '_blank')}
                                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs py-1 transition-colors"
                                  >
                                    View Full Size
                                  </button>
                                  <button
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = card.frontImage;
                                      link.download = `${selectedSubmission.reference_number}-front-${idx+1}.jpg`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 text-xs py-1 transition-colors border-l border-gray-300 flex items-center justify-center gap-1"
                                  >
                                    <Download size={12} />
                                    Download
                                  </button>
                                </div>
                              </div>
                            )}
                            {card.backImage && (
                              <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                                <div className="bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
                                  Card Back
                                </div>
                                <img 
                                  src={card.backImage} 
                                  alt="Card Back" 
                                  loading="lazy"
                                  className="w-full h-auto cursor-pointer hover:opacity-90"
                                  onClick={() => window.open(card.backImage, '_blank')}
                                />
                                <div className="flex">
                                  <button
                                    onClick={() => window.open(card.backImage, '_blank')}
                                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs py-1 transition-colors"
                                  >
                                    View Full Size
                                  </button>
                                  <button
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = card.backImage;
                                      link.download = `${selectedSubmission.reference_number}-back-${idx+1}.jpg`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 text-xs py-1 transition-colors border-l border-gray-300 flex items-center justify-center gap-1"
                                  >
                                    <Download size={12} />
                                    Download
                                  </button>
                                </div>
                              </div>
                            )}
                            {card.receiptImage && (
                              <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                                <div className="bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
                                  Receipt
                                </div>
                                <img 
                                  src={card.receiptImage} 
                                  alt="Receipt" 
                                  loading="lazy"
                                  className="w-full h-auto cursor-pointer hover:opacity-90"
                                  onClick={() => window.open(card.receiptImage, '_blank')}
                                />
                                <div className="flex">
                                  <button
                                    onClick={() => window.open(card.receiptImage, '_blank')}
                                    className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs py-1 transition-colors"
                                  >
                                    View Full Size
                                  </button>
                                  <button
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = card.receiptImage;
                                      link.download = `${selectedSubmission.reference_number}-receipt-${idx+1}.jpg`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 text-xs py-1 transition-colors border-l border-gray-300 flex items-center justify-center gap-1"
                                  >
                                    <Download size={12} />
                                    Download
                                  </button>
                                </div>
                              </div>
                            )}
                            {!card.frontImage && !card.backImage && !card.receiptImage && (
                              <p className="text-xs text-gray-500 italic col-span-3">No images uploaded</p>
                            )}
                          </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Internal Notes Section */}
                <div className="mt-6 border-t pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">📝 Internal Notes</h3>
                  
                  {/* Display existing notes */}
                  {selectedSubmission.internal_notes && selectedSubmission.internal_notes.length > 0 ? (
                    <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                      {selectedSubmission.internal_notes.map((note, idx) => (
                        <div key={idx} className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.text}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(note.timestamp).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })} • {note.added_by}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic mb-4">No notes yet</p>
                  )}

                  {/* Add new note */}
                  <div className="space-y-2">
                    <textarea
                      value={notesState[selectedSubmission.reference_number] || ''}
                      onChange={(e) => setNotesState(prev => ({
                        ...prev,
                        [selectedSubmission.reference_number]: e.target.value
                      }))}
                      placeholder="Add an internal note..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      rows="3"
                    />
                    <button
                      onClick={() => addNote(selectedSubmission.reference_number)}
                      disabled={notesLoading[selectedSubmission.reference_number] || !notesState[selectedSubmission.reference_number]?.trim()}
                      className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {notesLoading[selectedSubmission.reference_number] ? 'Adding Note...' : 'Add Note'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

          </>
        )}

        {/* Analytics Tab Content */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Analytics Header with Refresh Button */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Dashboard Analytics</h2>
                <p className="text-sm text-gray-500 mt-1">Overview of all submissions and key metrics</p>
              </div>
              <button
                onClick={() => fetchAnalytics(adminPassword)}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50 shadow-lg"
              >
                <svg 
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? 'Refreshing...' : 'Refresh Analytics'}
              </button>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-semibold">Total Submissions</p>
                    <p className="text-3xl font-bold mt-2">{analytics?.total_submissions || 0}</p>
                  </div>
                  <BarChart3 className="w-12 h-12 text-blue-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-semibold">Approved</p>
                    <p className="text-3xl font-bold mt-2">{analytics?.status_counts?.approved || 0}</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-semibold">Paid</p>
                    <p className="text-3xl font-bold mt-2">{analytics?.status_counts?.paid || 0}</p>
                  </div>
                  <DollarSign className="w-12 h-12 text-purple-200" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-sm font-semibold">Rejected</p>
                    <p className="text-3xl font-bold mt-2">{analytics?.status_counts?.rejected || 0}</p>
                  </div>
                  <XCircle className="w-12 h-12 text-red-200" />
                </div>
              </div>
            </div>
            
            {/* Status Breakdown */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Status Breakdown</h3>
              <div className="space-y-3">
                {['received', 'under_review', 'approved', 'paid', 'rejected', 'duplicate'].map((status) => {
                  const count = analytics?.status_counts?.[status] || 0;
                  const total = analytics?.total_submissions || 1;
                  const percentage = ((count / total) * 100).toFixed(1);
                  const colors = {
                    'received': 'bg-blue-500',
                    'under_review': 'bg-yellow-500',
                    'approved': 'bg-green-500',
                    'paid': 'bg-green-600',
                    'rejected': 'bg-red-500'
                  };
                  
                  return (
                    <div key={status}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-semibold text-gray-700 capitalize">{status.replace('_', ' ')}</span>
                        <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`${colors[status]} h-3 rounded-full transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Card Brands Analysis */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Top Card Brands</h3>
              <div className="space-y-3">
                {analytics?.top_brands && analytics.top_brands.length > 0 ? (
                  analytics.top_brands.map(([brand, count]) => {
                    const totalCards = analytics.top_brands.reduce((sum, [, c]) => sum + c, 0);
                    const percentage = totalCards > 0 ? ((count / totalCards) * 100).toFixed(1) : 0;
                    return (
                      <div key={brand}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-semibold text-gray-700">{brand}</span>
                          <span className="text-sm text-gray-600">{count} cards ({percentage}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-pink-500 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-sm">No brand data available</p>
                )}
              </div>
            </div>
            
            {/* Total Value */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Value Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-semibold">Total Value</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">${(analytics?.total_value || 0).toFixed(2)}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-semibold">Approved Value</p>
                  <p className="text-2xl font-bold text-green-900 mt-1">${(analytics?.approved_value || 0).toFixed(2)}</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-600 font-semibold">Paid Value</p>
                  <p className="text-2xl font-bold text-purple-900 mt-1">${(analytics?.paid_value || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Daily Submissions Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">📊 Daily Submissions</h3>
                  <p className="text-sm text-gray-500 mt-1">Track how many cards are submitted each day</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <select
                    value={dailyStatsDays}
                    onChange={(e) => {
                      const days = parseInt(e.target.value);
                      setDailyStatsDays(days);
                      fetchDailyStats(adminPassword, days);
                    }}
                    className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none text-sm font-semibold"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                  <button
                    onClick={() => fetchDailyStats(adminPassword, dailyStatsDays)}
                    disabled={dailyStatsLoading}
                    className="px-4 py-2 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 text-sm"
                  >
                    {dailyStatsLoading ? 'Loading...' : 'Refresh'}
                  </button>
                  {/* Download All Button */}
                  <button
                    onClick={() => {
                      const backendUrl = getBackendUrl();
                      const url = `${backendUrl}/api/admin/export-submissions?password=${encodeURIComponent(adminPassword)}&days=${dailyStatsDays}&exclude_duplicates=true`;
                      window.open(url, '_blank');
                    }}
                    className="px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors text-sm flex items-center gap-2"
                    title="Download all submissions (excluding duplicates)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="hidden sm:inline">Download All</span>
                  </button>
                </div>
              </div>

              {/* Period Totals */}
              {dailyStats?.totals && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-600 font-semibold">Total</p>
                    <p className="text-xl font-bold text-blue-900">{dailyStats.totals.total}</p>
                  </div>
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-cyan-600 font-semibold">Unique/New</p>
                    <p className="text-xl font-bold text-cyan-900">{dailyStats.totals.unique || 0}</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-orange-600 font-semibold">Duplicates</p>
                    <p className="text-xl font-bold text-orange-900">{dailyStats.totals.duplicates || 0}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600 font-semibold">Approved</p>
                    <p className="text-xl font-bold text-green-900">{dailyStats.totals.approved}</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-purple-600 font-semibold">Paid</p>
                    <p className="text-xl font-bold text-purple-900">{dailyStats.totals.paid}</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-yellow-600 font-semibold">Pending</p>
                    <p className="text-xl font-bold text-yellow-900">{dailyStats.totals.pending}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-red-600 font-semibold">Rejected</p>
                    <p className="text-xl font-bold text-red-900">{dailyStats.totals.rejected}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600 font-semibold">Value</p>
                    <p className="text-xl font-bold text-gray-900">${dailyStats.totals.total_value}</p>
                  </div>
                </div>
              )}

              {/* Daily Stats Table */}
              {dailyStatsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading daily stats...</p>
                </div>
              ) : dailyStats?.daily_stats && dailyStats.daily_stats.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-3 text-left font-bold text-gray-700">Date</th>
                        <th className="px-3 py-3 text-center font-bold text-blue-700">Total</th>
                        <th className="px-3 py-3 text-center font-bold text-cyan-700">Unique</th>
                        <th className="px-3 py-3 text-center font-bold text-orange-700">Dupes</th>
                        <th className="px-3 py-3 text-center font-bold text-green-700">Approved</th>
                        <th className="px-3 py-3 text-center font-bold text-purple-700">Paid</th>
                        <th className="px-3 py-3 text-center font-bold text-yellow-700">Pending</th>
                        <th className="px-3 py-3 text-center font-bold text-red-700">Rejected</th>
                        <th className="px-3 py-3 text-right font-bold text-gray-700">Value</th>
                        <th className="px-3 py-3 text-center font-bold text-gray-700">Export</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyStats.daily_stats.map((day, index) => {
                        const dateObj = new Date(day.date + 'T00:00:00');
                        const formattedDate = dateObj.toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        });
                        return (
                          <tr key={day.date} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-3 font-semibold text-gray-800">{formattedDate}</td>
                            <td className="px-3 py-3 text-center">
                              <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 bg-blue-100 text-blue-800 font-bold rounded-full">
                                {day.total}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full font-bold ${(day.unique || 0) > 0 ? 'bg-cyan-100 text-cyan-800' : 'text-gray-400'}`}>
                                {day.unique || 0}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full font-semibold ${(day.duplicates || 0) > 0 ? 'bg-orange-100 text-orange-800' : 'text-gray-400'}`}>
                                {day.duplicates || 0}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full font-semibold ${day.approved > 0 ? 'bg-green-100 text-green-800' : 'text-gray-400'}`}>
                                {day.approved}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full font-semibold ${day.paid > 0 ? 'bg-purple-100 text-purple-800' : 'text-gray-400'}`}>
                                {day.paid}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full font-semibold ${day.pending > 0 ? 'bg-yellow-100 text-yellow-800' : 'text-gray-400'}`}>
                                {day.pending}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-full font-semibold ${day.rejected > 0 ? 'bg-red-100 text-red-800' : 'text-gray-400'}`}>
                                {day.rejected}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-right font-semibold text-gray-700">
                              ${day.total_value.toFixed(2)}
                            </td>
                            <td className="px-3 py-3 text-center">
                              <button
                                onClick={() => {
                                  const backendUrl = getBackendUrl();
                                  const url = `${backendUrl}/api/admin/export-submissions?password=${encodeURIComponent(adminPassword)}&date=${day.date}&exclude_duplicates=true`;
                                  window.open(url, '_blank');
                                }}
                                className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                title={`Download ${formattedDate} submissions (CSV)`}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No submission data available for the selected period.</p>
                  <p className="text-sm mt-2">Try selecting a different date range.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab Content */}
        {activeTab === 'settings' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
              <button
                onClick={() => {
                  setAdminPassword('');
                  setIsAuthenticated(false);
                  setActiveTab('submissions');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
            
            {/* Password Change Section */}
            <div className="max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Admin Password
              </h3>
              
              {passwordChangeMessage.text && (
                <div className={`mb-4 p-4 rounded-lg ${
                  passwordChangeMessage.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {passwordChangeMessage.text}
                </div>
              )}
              
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordChange.current}
                    onChange={(e) => setPasswordChange({...passwordChange, current: e.target.value})}
                    placeholder="Enter current password"
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordChange.new}
                    onChange={(e) => setPasswordChange({...passwordChange, new: e.target.value})}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    minLength={8}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordChange.confirm}
                    onChange={(e) => setPasswordChange({...passwordChange, confirm: e.target.value})}
                    placeholder="Confirm new password"
                    required
                    minLength={8}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </button>
              </form>
              
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Security Tips:</strong>
                  <br/>• Use at least 8 characters
                  <br/>• Include uppercase, lowercase, numbers and symbols
                  <br/>• Don't reuse passwords from other accounts
                </p>
              </div>
            </div>

            {/* Auto-Refresh Settings Section */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Auto-Refresh Settings
              </h3>
              
              <div className="max-w-2xl space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Smart Auto-Refresh:</strong> Dashboard automatically refreshes to show new submissions when you're not actively working (no modals open, not typing).
                  </p>
                </div>

                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-semibold text-gray-900">Enable Auto-Refresh</p>
                    <p className="text-sm text-gray-600 mt-1">Automatically fetch new submissions in the background</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoRefreshEnabled}
                      onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:ring-4 peer-focus:ring-pink-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-pink-600"></div>
                  </label>
                </div>

                {/* Refresh Interval Selector */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block font-semibold text-gray-900 mb-2">
                    Refresh Interval
                  </label>
                  <select
                    value={autoRefreshInterval}
                    onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
                    disabled={!autoRefreshEnabled}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value={30}>Every 30 seconds</option>
                    <option value={60}>Every 1 minute (Recommended)</option>
                    <option value={120}>Every 2 minutes</option>
                    <option value={300}>Every 5 minutes</option>
                  </select>
                  <p className="text-sm text-gray-600 mt-2">
                    {autoRefreshEnabled 
                      ? `Dashboard will refresh every ${autoRefreshInterval < 60 ? autoRefreshInterval + ' seconds' : (autoRefreshInterval / 60) + ' minute(s)'} when idle`
                      : 'Auto-refresh is currently disabled'}
                  </p>
                </div>

                {/* Last Refresh Time */}
                {lastRefreshTime && autoRefreshEnabled && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                    <strong>Last auto-refresh:</strong> {lastRefreshTime.toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>

            {/* Auto-Purge Settings Section */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Auto-Purge Data Management
              </h3>

              {!purgeStats && (
                <button
                  onClick={fetchPurgeStats}
                  className="mb-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Load Purge Settings
                </button>
              )}

              {purgeStats && (
                <div className="max-w-2xl">
                  {purgeMessage.text && (
                    <div className={`mb-4 p-4 rounded-lg ${
                      purgeMessage.type === 'success' 
                        ? 'bg-green-50 border border-green-200 text-green-800' 
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                      {purgeMessage.text}
                    </div>
                  )}

                  {/* Current Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-600 font-semibold">Total Submissions</p>
                      <p className="text-2xl font-bold text-blue-900 mt-1">{purgeStats.total_submissions}</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-sm text-orange-600 font-semibold">Ready to Purge</p>
                      <p className="text-2xl font-bold text-orange-900 mt-1">{purgeStats.submissions_to_purge}</p>
                      <p className="text-xs text-orange-600 mt-1">Older than {purgeStats.auto_purge_days} days</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="text-sm text-purple-600 font-semibold">Current Period</p>
                      <p className="text-2xl font-bold text-purple-900 mt-1">{purgeStats.auto_purge_days} days</p>
                      <p className="text-xs text-purple-600 mt-1">{purgeStats.next_purge_estimate}</p>
                    </div>
                  </div>

                  {/* Update Purge Period */}
                  <form onSubmit={handleUpdatePurgeSettings} className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Change Auto-Purge Period
                    </label>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <select
                          value={newPurgeDays}
                          onChange={(e) => setNewPurgeDays(e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                        >
                          <option value="7">7 days (1 week)</option>
                          <option value="14">14 days (2 weeks)</option>
                          <option value="21">21 days (3 weeks)</option>
                          <option value="30">30 days (1 month)</option>
                          <option value="45">45 days (1.5 months)</option>
                          <option value="60">60 days (2 months)</option>
                          <option value="90">90 days (3 months)</option>
                        </select>
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50"
                      >
                        {loading ? 'Updating...' : 'Update Period'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Submissions older than the selected period will be automatically deleted daily
                    </p>
                  </form>

                  {/* Manual Purge Button */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-yellow-900 mb-1">Manual Purge</p>
                        <p className="text-sm text-yellow-700 mb-3">
                          Delete {purgeStats.submissions_to_purge} submission(s) older than {purgeStats.auto_purge_days} days right now. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleManualPurge}
                      disabled={loading || purgeStats.submissions_to_purge === 0}
                      className="px-6 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Purging...' : `Delete ${purgeStats.submissions_to_purge} Old Submissions`}
                    </button>
                  </div>

                  {/* Info Box */}
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Auto-Purge Information:</strong>
                      <br/>• Runs automatically every 24 hours
                      <br/>• Deletes submissions, card images, and fraud records
                      <br/>• Helps maintain database performance
                      <br/>• Changes take effect immediately
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Reject Existing Submissions Section */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Bulk Reject Submissions Under $50
              </h3>

              {bulkRejectMessage.text && (
                <div className={`mb-4 p-4 rounded-lg ${
                  bulkRejectMessage.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {bulkRejectMessage.text}
                </div>
              )}

              <div className="max-w-2xl">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-orange-800 mb-3">
                    <strong>What this does:</strong>
                    <br/>• Finds all submissions with status = "received"
                    <br/>• Checks if ALL cards in each submission are under $50
                    <br/>• Marks those submissions as "rejected"
                    <br/>• Skips submissions with: approved, paid, under_review, or rejected status
                    <br/>• Skips submissions that have at least one card ≥ $50
                  </p>
                  
                  {!bulkRejectPreview && (
                    <button
                      onClick={handleBulkRejectPreview}
                      disabled={loading}
                      className="px-6 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all disabled:opacity-50"
                    >
                      {loading ? 'Loading...' : 'Preview What Will Be Rejected'}
                    </button>
                  )}
                </div>

                {bulkRejectPreview && (
                  <div className="bg-white border-2 border-orange-300 rounded-lg p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Preview Results</h4>
                    
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600 font-semibold">Total Received</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{bulkRejectPreview.summary.total_received}</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-600 font-semibold">Will Reject</p>
                        <p className="text-2xl font-bold text-red-900 mt-1">{bulkRejectPreview.summary.to_reject}</p>
                        <p className="text-xs text-red-600 mt-1">All cards &lt; $50</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-green-600 font-semibold">Will Keep</p>
                        <p className="text-2xl font-bold text-green-900 mt-1">{bulkRejectPreview.summary.to_keep}</p>
                        <p className="text-xs text-green-600 mt-1">Has card ≥ $50</p>
                      </div>
                    </div>

                    {bulkRejectPreview.samples_to_reject && bulkRejectPreview.samples_to_reject.length > 0 && (
                      <div className="mb-6">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Sample Submissions to Reject (first 10):</p>
                        <div className="bg-gray-50 border border-gray-200 rounded p-3 max-h-48 overflow-y-auto">
                          {bulkRejectPreview.samples_to_reject.map((sample, idx) => (
                            <div key={idx} className="text-xs text-gray-600 mb-1">
                              {idx + 1}. {sample.reference_number} - Cards: ${sample.card_values.join(', $')} - {sample.email}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={handleBulkRejectExecute}
                        disabled={loading || bulkRejectPreview.summary.to_reject === 0}
                        className="px-6 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Executing...' : `Reject ${bulkRejectPreview.summary.to_reject} Submissions`}
                      </button>
                      <button
                        onClick={() => setBulkRejectPreview(null)}
                        disabled={loading}
                        className="px-6 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>

                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-xs text-yellow-800">
                        <strong>⚠️ Important:</strong> This action will update the status of {bulkRejectPreview.summary.to_reject} submissions. 
                        Customers will see the rejection when they check their tracking page. You can manually change the status later if needed.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Update Status Modal */}
        {showUpdateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Update Status</h2>
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Reference Number</p>
                  <p className="font-semibold text-gray-900">{statusUpdate.ref}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Status
                  </label>
                  <select
                    value={statusUpdate.status}
                    onChange={(e) => setStatusUpdate({...statusUpdate, status: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="received">Received</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                    <option value="duplicate">Duplicate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Customer Message (Optional)
                  </label>
                  <textarea
                    value={statusUpdate.message}
                    onChange={(e) => setStatusUpdate({...statusUpdate, message: e.target.value})}
                    placeholder="Optional message to send to customer..."
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => updateStatus(statusUpdate.ref, statusUpdate.status, statusUpdate.message)}
                    disabled={!statusUpdate.status || updateLoading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50"
                  >
                    {updateLoading ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Update Modal */}
        {showBulkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Bulk Update {selectedSubmissions.length} Submission{selectedSubmissions.length > 1 ? 's' : ''}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Status
                  </label>
                  <select
                    value={bulkAction.status}
                    onChange={(e) => setBulkAction({...bulkAction, status: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="received">Received</option>
                    <option value="under_review">Under Review</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                    <option value="duplicate">Duplicate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Customer Message (Applied to All)
                  </label>
                  <textarea
                    value={bulkAction.message}
                    onChange={(e) => setBulkAction({...bulkAction, message: e.target.value})}
                    placeholder="E.g., 'Unsupported card' - This message will be sent to all selected customers"
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This message will appear on the tracking page for all selected submissions
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> This will update all {selectedSubmissions.length} selected submission{selectedSubmissions.length > 1 ? 's' : ''} with the same status and message.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowBulkModal(false);
                      setBulkAction({ status: '', message: '' });
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkUpdate}
                    disabled={!bulkAction.status || updateLoading}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50"
                  >
                    {updateLoading ? 'Updating...' : `Update ${selectedSubmissions.length} Submission${selectedSubmissions.length > 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support Queries Tab Content */}
        {activeTab === 'support' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <MessageCircle className="w-6 h-6 text-pink-600" />
                    AI Chat Support Queries
                  </h2>
                  <p className="text-gray-600 mt-1">Monitor customer chat inquiries and payment issues</p>
                </div>
                <button
                  onClick={() => fetchSupportQueries(adminPassword)}
                  className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {/* Filter */}
              <div className="mb-4 flex gap-2">
                <button
                  onClick={() => {
                    setSupportFilter('all');
                    fetchSupportQueries(adminPassword);
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    supportFilter === 'all' 
                      ? 'bg-pink-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  All ({supportQueries.length})
                </button>
                <button
                  onClick={() => {
                    setSupportFilter('pending');
                    fetchSupportQueries(adminPassword);
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    supportFilter === 'pending' 
                      ? 'bg-pink-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Pending ({supportQueries.filter(q => !q.resolved).length})
                </button>
                <button
                  onClick={() => {
                    setSupportFilter('resolved');
                    fetchSupportQueries(adminPassword);
                  }}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    supportFilter === 'resolved' 
                      ? 'bg-pink-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Resolved ({supportQueries.filter(q => q.resolved).length})
                </button>
              </div>

              {/* Queries Table */}
              {supportQueries.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg font-semibold">No support queries yet</p>
                  <p className="text-gray-400 text-sm mt-2">Customer chat inquiries will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date & Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reference #</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Issue Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Summary</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {supportQueries.map((query) => (
                        <tr key={query.session_id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(query.timestamp).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <div>
                              <p className="font-semibold text-gray-900">{query.customer_name}</p>
                              <p className="text-gray-500 text-xs">{query.customer_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-pink-600">
                            {query.reference_number || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {query.issue_type}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate">
                            {query.issue_summary}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              query.resolved
                                ? 'bg-green-100 text-green-800'
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {query.resolved ? 'Resolved' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm space-x-2">
                            <button
                              onClick={() => handleViewChat(query)}
                              className="text-blue-600 hover:text-blue-800 font-semibold"
                            >
                              View Chat
                            </button>
                            {!query.resolved && (
                              <button
                                onClick={() => markQueryResolved(query.session_id)}
                                className="text-green-600 hover:text-green-800 font-semibold"
                              >
                                Mark Resolved
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">💡 How the AI Chat System Works:</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>• <strong>Auto-Response:</strong> AI automatically checks submission status and responds to customers</li>
                <li>• <strong>Smart Detection:</strong> Detects reference numbers (GC-XXXXXX-XX) and fetches real-time status</li>
                <li>• <strong>Logged Queries:</strong> Only important inquiries (payment issues, status checks) are logged here</li>
                <li>• <strong>Human Escalation:</strong> Automatically transfers to Zoho when customer asks for manager/supervisor</li>
                <li>• <strong>Multiple Chats:</strong> Handles unlimited simultaneous conversations independently</li>
              </ul>
            </div>
          </div>
        )}

        {/* Chat Transcript Modal */}
        {showTranscriptModal && selectedTranscript && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Chat Transcript</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedTranscript.customer_name} ({selectedTranscript.customer_email})
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>Session: {selectedTranscript.session_id}</span>
                    <span>Issue: {selectedTranscript.issue_type}</span>
                    {selectedTranscript.reference_number && (
                      <span className="font-mono text-pink-600">Ref: {selectedTranscript.reference_number}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowTranscriptModal(false);
                    setSelectedTranscript(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
                {selectedTranscript.chat_transcript && selectedTranscript.chat_transcript.length > 0 ? (
                  selectedTranscript.chat_transcript.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                          : 'bg-white text-gray-900 border border-gray-200'
                      } rounded-lg p-4 shadow-sm`}>
                        <div className="flex items-center gap-2 mb-2">
                          {message.role === 'user' ? (
                            <User className="w-4 h-4" />
                          ) : (
                            <MessageCircle className="w-4 h-4" />
                          )}
                          <span className="text-xs font-semibold">
                            {message.role === 'user' ? selectedTranscript.customer_name : 'AI Assistant (Sarah)'}
                          </span>
                          <span className={`text-xs ml-auto ${
                            message.role === 'user' ? 'text-white/70' : 'text-gray-500'
                          }`}>
                            {new Date(message.timestamp).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {message.content}
                        </div>
                      </div>
                    </div>
                  ))
                ) : selectedTranscript.loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-3"></div>
                    <p className="text-gray-500">Loading chat transcript...</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No chat transcript available for this session</p>
                    <p className="text-sm mt-2">Messages may have been cleared or not saved</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold">{selectedTranscript.message_count || 0}</span> messages
                    {selectedTranscript.timestamp && (
                      <span className="ml-4">
                        Ended: {new Date(selectedTranscript.timestamp).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowTranscriptModal(false);
                      setSelectedTranscript(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All Chats Tab Content */}
        {activeTab === 'allchats' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">All Chat Sessions</h2>
                <button
                  onClick={fetchAllChatSessions}
                  className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              <div className="mb-4 flex gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-900">Total: {allChatSessions.length}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-900">
                    Resolved: {allChatSessions.filter(s => s.resolved).length}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                  <span className="text-sm font-semibold text-orange-900">
                    Active: {allChatSessions.filter(s => !s.resolved).length}
                  </span>
                </div>
              </div>

              {allChatSessions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No chat sessions yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-pink-50 to-purple-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Reference</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Issue Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Messages</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {allChatSessions.map((session, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                            {new Date(session.last_message).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">{session.customer_name}</div>
                            <div className="text-xs text-gray-500">{session.customer_email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-pink-600">
                            {session.reference_number || '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                            {session.issue_type}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                            <span className="px-2 py-1 bg-gray-100 rounded-full">
                              {session.message_count} messages
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              session.resolved
                                ? 'bg-green-100 text-green-800'
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {session.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => {
                                setSelectedTranscript(session);
                                setShowTranscriptModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 font-semibold"
                            >
                              View Chat
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">💡 About All Chats View:</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>• <strong>Active Sessions:</strong> Ongoing conversations that haven't been ended yet</li>
                <li>• <strong>Resolved Sessions:</strong> Conversations that customers have ended or completed</li>
                <li>• <strong>Full Transcripts:</strong> View complete conversation history for ALL sessions</li>
                <li>• <strong>AI Training:</strong> Use these transcripts to identify patterns and improve AI responses</li>
                <li>• <strong>Real-Time:</strong> See what customers are asking right now to monitor AI performance</li>
              </ul>
            </div>
          </div>
        )}

        {/* AI Learning Tab Content */}
        {activeTab === 'learning' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">AI Learning Queue</h2>
                <button
                  onClick={() => fetchLearningQueue('pending')}
                  className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              <div className="mb-4 flex gap-4">
                <button
                  onClick={() => fetchLearningQueue('pending')}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                >
                  <Clock className="w-5 h-5 text-orange-600" />
                  <span className="text-sm font-semibold text-orange-900">
                    Pending: {learningQueue.filter(i => !i.reviewed).length}
                  </span>
                </button>
                <button
                  onClick={() => fetchLearningQueue('reviewed')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-900">
                    Reviewed: {learningQueue.filter(i => i.reviewed).length}
                  </span>
                </button>
              </div>

              {learningQueue.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Zap className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="font-semibold mb-2">No questions in learning queue yet</p>
                  <p className="text-sm">The AI will automatically flag questions it's unsure about</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {learningQueue.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              item.reviewed
                                ? 'bg-green-100 text-green-800'
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {item.reviewed ? 'Reviewed' : 'Needs Review'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(item.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-1">Customer Question:</p>
                            <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{item.question}</p>
                          </div>
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-700 mb-1">AI's Response (Uncertain):</p>
                            <p className="text-sm text-red-700 bg-red-50 p-3 rounded italic">{item.ai_response}</p>
                          </div>
                          {item.reviewed && item.admin_answer && (
                            <div className="mb-3">
                              <p className="text-sm font-semibold text-gray-700 mb-1">Your Answer:</p>
                              <p className="text-sm text-green-900 bg-green-50 p-3 rounded">{item.admin_answer}</p>
                              {item.added_to_kb && (
                                <p className="text-xs text-green-600 mt-1">✓ Added to Knowledge Base</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {!item.reviewed && (
                        <button
                          onClick={() => {
                            setSelectedLearningItem(item);
                            setShowLearningModal(true);
                          }}
                          className="w-full px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors font-semibold"
                        >
                          Provide Answer & Train AI
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-4">
              <h3 className="font-semibold text-pink-900 mb-2">🤖 How AI Self-Learning Works:</h3>
              <ul className="space-y-2 text-sm text-pink-800">
                <li>• <strong>Automatic Detection:</strong> AI flags questions when it's uncertain or can't provide accurate answers</li>
                <li>• <strong>Admin Review:</strong> You review flagged questions and provide the correct answer</li>
                <li>• <strong>Knowledge Base:</strong> Your answers are automatically added to the AI's knowledge base</li>
                <li>• <strong>Continuous Improvement:</strong> AI learns from your feedback and gets smarter over time</li>
                <li>• <strong>Zero Wrong Answers:</strong> AI asks for help instead of guessing - maintaining high quality service</li>
              </ul>
            </div>
          </div>
        )}

        {/* Learning Answer Modal */}
        {showLearningModal && selectedLearningItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Teach the AI</h3>
                  <p className="text-sm text-gray-600 mt-1">Provide the correct answer to improve AI responses</p>
                </div>
                <button
                  onClick={() => {
                    setShowLearningModal(false);
                    setAdminAnswer('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer Question:</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-900">{selectedLearningItem.question}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">AI's Response (Uncertain):</p>
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <p className="text-red-900 italic">{selectedLearningItem.ai_response}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Your Answer (This will be the AI's new response):</p>
                  <textarea
                    value={adminAnswer}
                    onChange={(e) => setAdminAnswer(e.target.value)}
                    placeholder="Provide a clear, accurate answer that the AI should give for this question..."
                    className="w-full h-40 px-4 py-3 border-2 border-pink-300 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Tip: Write this as if you're the AI responding to the customer. Be clear, friendly, and accurate.
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                  <button
                    onClick={() => provideLearningAnswer(selectedLearningItem.id, adminAnswer, true)}
                    disabled={!adminAnswer.trim()}
                    className="flex-1 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
                  >
                    ✓ Save & Add to Knowledge Base
                  </button>
                  <button
                    onClick={() => provideLearningAnswer(selectedLearningItem.id, adminAnswer, false)}
                    disabled={!adminAnswer.trim()}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors font-semibold"
                  >
                    Save Only (Don't Add to KB)
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2 text-center">
                  Recommended: Add to Knowledge Base so the AI can automatically use this answer in future
                </p>
              </div>
            </div>
          </div>
        )}        {/* AI Training Tab Content */}
        {activeTab === 'training' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Zap className="w-6 h-6 text-pink-600" />
                    AI Training - Knowledge Base
                  </h2>
                  <p className="text-gray-600 mt-1">Train your AI without redeployment! Add Q&A pairs that AI will use instantly.</p>
                </div>
                <button
                  onClick={() => {
                    setEditingQA(null);
                    setNewQA({ question: '', answer: '', category: 'General', keywords: '' });
                    setShowAddQAModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all font-semibold"
                >
                  + Add Q&A
                </button>
              </div>

              {/* Knowledge Base Table */}
              {knowledgeBase.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg font-semibold">No Q&A pairs yet</p>
                  <p className="text-gray-400 text-sm mt-2">Add your first Q&A pair to start training the AI</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Question</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Answer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Used</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {knowledgeBase.map((qa) => (
                        <tr key={qa.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 text-sm text-gray-900 font-semibold max-w-xs">
                            {qa.question}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700 max-w-md">
                            <div className="truncate">{qa.answer}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {qa.category}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {qa.usage_count} times
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm space-x-2">
                            <button
                              onClick={() => {
                                setEditingQA(qa);
                                setNewQA({
                                  question: qa.question,
                                  answer: qa.answer,
                                  category: qa.category,
                                  keywords: qa.keywords ? qa.keywords.join(', ') : ''
                                });
                                setShowAddQAModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 font-semibold mr-3"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteQAPair(qa.id)}
                              className="text-red-600 hover:text-red-800 font-semibold"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Info Card */}
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-4">
              <h3 className="font-semibold text-pink-900 mb-2">💡 How It Works:</h3>
              <ul className="space-y-2 text-sm text-pink-800">
                <li>• <strong>Instant Updates:</strong> Changes apply immediately - no deployment needed!</li>
                <li>• <strong>Priority Matching:</strong> AI checks your Q&A first before generating responses</li>
                <li>• <strong>Smart Search:</strong> Matches similar questions automatically using keywords</li>
                <li>• <strong>Usage Tracking:</strong> See how often each answer is used</li>
              </ul>
            </div>
          </div>
        )}

        {/* Add/Edit Q&A Modal */}
        {showAddQAModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-6 text-white">
                <h3 className="text-xl font-bold">{editingQA ? 'Edit Q&A Pair' : 'Add New Q&A Pair'}</h3>
                <p className="text-sm text-pink-100 mt-1">{editingQA ? 'Update your AI training data' : 'Train your AI with custom responses'}</p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Question *</label>
                  <input
                    type="text"
                    value={newQA.question}
                    onChange={(e) => setNewQA({...newQA, question: e.target.value})}
                    placeholder="e.g., Why is payment taking so long?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Answer *</label>
                  <textarea
                    value={newQA.answer}
                    onChange={(e) => setNewQA({...newQA, answer: e.target.value})}
                    placeholder="Our validation team takes 24-48 hours to verify cards..."
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select
                    value={newQA.category}
                    onChange={(e) => setNewQA({...newQA, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    <option value="General">General</option>
                    <option value="Payment Issues">Payment Issues</option>
                    <option value="Submission Status">Submission Status</option>
                    <option value="Card Acceptance">Card Acceptance</option>
                    <option value="Processing Time">Processing Time</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Keywords (optional)</label>
                  <input
                    type="text"
                    value={newQA.keywords}
                    onChange={(e) => setNewQA({...newQA, keywords: e.target.value})}
                    placeholder="payment, delay, slow (comma separated)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Help AI match questions better</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={editingQA ? updateQAPair : addQAPair}
                    className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-purple-700 transition-all"
                  >
                    {editingQA ? 'Update Q&A Pair' : 'Add Q&A Pair'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddQAModal(false);
                      setEditingQA(null);
                      setNewQA({ question: '', answer: '', category: 'General', keywords: '' });
                    }}
                    className="px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* BLOG MANAGEMENT TAB                         */}
        {/* ============================================ */}
        {activeTab === 'blog' && (
          <div className="space-y-6" data-testid="blog-management-tab">
            {/* Blog Stats Cards */}
            {blogStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="text-sm text-gray-500">Published Articles</div>
                  <div className="text-2xl font-bold text-green-600" data-testid="blog-published-count">{blogStats.published_articles}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="text-sm text-gray-500">Pending Topics</div>
                  <div className="text-2xl font-bold text-blue-600" data-testid="blog-pending-count">{blogStats.pending_topics}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="text-sm text-gray-500">Total Topics</div>
                  <div className="text-2xl font-bold text-gray-800">{blogStats.total_topics}</div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="text-sm text-gray-500">Schedule</div>
                  <div className="text-sm font-semibold text-purple-600">{blogStats.schedule}</div>
                </div>
              </div>
            )}

            {/* Actions Bar */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm flex flex-wrap items-center gap-3">
              <button
                onClick={handleGenerateArticle}
                disabled={blogGenerating}
                className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2"
                data-testid="blog-generate-btn"
              >
                {blogGenerating ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Generating...</>
                ) : (
                  <><Zap className="w-4 h-4" /> Generate Next Article</>
                )}
              </button>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setBlogView('articles')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${blogView === 'articles' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                  data-testid="blog-view-articles-btn"
                >
                  Articles ({blogArticles.length})
                </button>
                <button
                  onClick={() => setBlogView('topics')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${blogView === 'topics' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                  data-testid="blog-view-topics-btn"
                >
                  Topic Queue ({blogTopics.length})
                </button>
              </div>
              {blogView === 'articles' && (
                <select
                  value={blogFilter}
                  onChange={(e) => setBlogFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  data-testid="blog-filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="unpublished">Unpublished</option>
                </select>
              )}
              <button
                onClick={fetchBlogData}
                disabled={blogLoading}
                className="ml-auto text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition"
                data-testid="blog-refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${blogLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Message Banner */}
            {blogMessage.text && (
              <div className={`rounded-lg px-4 py-3 text-sm font-medium flex items-center justify-between ${
                blogMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                blogMessage.type === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                'bg-red-50 text-red-700 border border-red-200'
              }`} data-testid="blog-message-banner">
                {blogMessage.text}
                <button onClick={() => setBlogMessage({ text: '', type: '' })} className="ml-3 opacity-60 hover:opacity-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Articles View */}
            {blogView === 'articles' && (
              <div className="space-y-3">
                {blogLoading ? (
                  <div className="text-center py-12 text-gray-400">Loading articles...</div>
                ) : (
                  (blogFilter === 'all' ? blogArticles : blogArticles.filter(a => a.status === blogFilter)).map(article => (
                    <div key={article.slug} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-md transition" data-testid={`blog-article-card-${article.slug}`}>
                      <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                              article.status === 'published' ? 'bg-green-100 text-green-700' :
                              article.status === 'draft' ? 'bg-gray-100 text-gray-600' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {article.status}
                            </span>
                            <span className="text-xs text-gray-400">{article.category}</span>
                            {article.generated_by === 'ai' && <span className="text-xs text-blue-500 font-medium">AI</span>}
                          </div>
                          <h4 className="font-semibold text-gray-900 truncate">{article.title}</h4>
                          <p className="text-sm text-gray-500 truncate mt-0.5">{article.excerpt}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span>Slug: /{article.slug}</span>
                            <span>{article.read_time || 8} min read</span>
                            {article.publish_date && <span>{new Date(article.publish_date).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {article.status === 'published' ? (
                            <button
                              onClick={() => handleUpdateArticleStatus(article.slug, 'unpublished')}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-yellow-300 text-yellow-700 hover:bg-yellow-50 transition"
                              data-testid={`unpublish-${article.slug}`}
                            >
                              Unpublish
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUpdateArticleStatus(article.slug, 'published')}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-green-300 text-green-700 hover:bg-green-50 transition"
                              data-testid={`publish-${article.slug}`}
                            >
                              Publish
                            </button>
                          )}
                          <button
                            onClick={() => { setEditingArticle({...article}); setShowBlogEditModal(true); }}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                            data-testid={`edit-${article.slug}`}
                          >
                            Edit
                          </button>
                          <a
                            href={`/blog/${article.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50 transition"
                          >
                            View
                          </a>
                          <button
                            onClick={() => handleDeleteArticle(article.slug)}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition"
                            data-testid={`delete-${article.slug}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {!blogLoading && blogArticles.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Zap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No articles yet</p>
                    <p className="text-sm text-gray-400 mt-1">Click "Generate Next Article" to create your first AI article</p>
                  </div>
                )}
              </div>
            )}

            {/* Topics Queue View */}
            {blogView === 'topics' && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Topic</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Intent</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Priority</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blogTopics.map((topic, i) => (
                        <tr key={topic.slug || i} className="border-b border-gray-100 hover:bg-gray-50 transition">
                          <td className="px-4 py-3 text-gray-500">{topic.order || i + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{topic.title}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{topic.primary_keyword}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              topic.intent_type === 'Transactional' ? 'bg-green-100 text-green-700' :
                              topic.intent_type === 'Brand-Specific' ? 'bg-purple-100 text-purple-700' :
                              topic.intent_type === 'Commercial Investigation' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {topic.intent_type}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`font-semibold ${
                              topic.priority === 1 ? 'text-red-600' :
                              topic.priority === 2 ? 'text-orange-500' :
                              topic.priority === 3 ? 'text-yellow-600' : 'text-gray-400'
                            }`}>
                              P{topic.priority}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                              topic.status === 'published' ? 'bg-green-100 text-green-700' :
                              topic.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              topic.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              {topic.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Blog Edit Modal */}
        {showBlogEditModal && editingArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">Edit Article</h3>
                  <button onClick={() => { setShowBlogEditModal(false); setEditingArticle(null); }} className="text-white hover:bg-white/20 rounded-full p-1.5 transition">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editingArticle.title || ''}
                    onChange={(e) => setEditingArticle({...editingArticle, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    data-testid="blog-edit-title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Excerpt</label>
                  <textarea
                    value={editingArticle.excerpt || ''}
                    onChange={(e) => setEditingArticle({...editingArticle, excerpt: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    data-testid="blog-edit-excerpt"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Meta Description</label>
                  <textarea
                    value={editingArticle.meta_description || ''}
                    onChange={(e) => setEditingArticle({...editingArticle, meta_description: e.target.value})}
                    rows={2}
                    maxLength={160}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    data-testid="blog-edit-meta"
                  />
                  <div className="text-xs text-gray-400 mt-1">{(editingArticle.meta_description || '').length}/160</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                    <select
                      value={editingArticle.category || 'Guides'}
                      onChange={(e) => setEditingArticle({...editingArticle, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    >
                      <option value="Guides">Guides</option>
                      <option value="Brand Guides">Brand Guides</option>
                      <option value="Payments">Payments</option>
                      <option value="Security">Security</option>
                      <option value="Comparison">Comparison</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                    <select
                      value={editingArticle.status || 'published'}
                      onChange={(e) => setEditingArticle({...editingArticle, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    >
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="unpublished">Unpublished</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSaveArticleEdit}
                    className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg transition"
                    data-testid="blog-edit-save-btn"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => { setShowBlogEditModal(false); setEditingArticle(null); }}
                    className="px-6 py-2.5 rounded-lg font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer History Modal */}
        {showHistoryModal && customerHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">Customer History</h3>
                    <p className="text-blue-100 mt-1">{customerHistory.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowHistoryModal(false);
                      setCustomerHistory(null);
                    }}
                    className="text-white hover:bg-blue-700 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Total Submissions:</strong> {customerHistory.submissions.length}
                  </p>
                </div>

                {customerHistory.submissions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No submissions found for this customer.</p>
                ) : (
                  <div className="space-y-4">
                    {customerHistory.submissions.map((sub, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-bold text-gray-900">{sub.reference_number}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(sub.submitted_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            sub.status === 'approved' ? 'bg-green-100 text-green-800' :
                            sub.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            sub.status === 'paid' ? 'bg-purple-100 text-purple-800' :
                            sub.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {sub.status}
                          </span>
                        </div>

                        <div className="space-y-1">
                          <p className="text-sm text-gray-700"><strong>Cards:</strong></p>
                          {sub.cards && sub.cards.map((card, cardIdx) => (
                            <div key={cardIdx} className="text-sm text-gray-600 ml-4">
                              • {card.brand} - ${card.value} ({card.cardType})
                            </div>
                          ))}
                        </div>

                        {sub.customer_message && (
                          <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                            <p className="text-xs text-gray-500 font-semibold mb-1">Message:</p>
                            <p className="text-sm text-gray-700">{sub.customer_message}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setCustomerHistory(null);
                  }}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Floating Action Button - Quick Refresh (only on submissions tab) */}
        {activeTab === 'submissions' && isAuthenticated && (
          <div className="lg:hidden fixed bottom-6 right-6 z-50">
            <button
              onClick={() => fetchSubmissions(adminPassword)}
              disabled={loading}
              className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[64px] min-h-[64px] group"
              aria-label="Quick Refresh"
            >
              <RefreshCw className={`w-7 h-7 ${loading ? 'animate-spin' : 'group-active:rotate-180 transition-transform duration-300'}`} />
            </button>
            {!loading && filteredSubmissions.length > 0 && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-lg">
                {filteredSubmissions.length}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
